import { useState, useEffect, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import ReactQuill from 'react-quill-new'
import 'react-quill-new/dist/quill.snow.css'
import { apiClient } from '../services/api'
import { env } from '../env'
import { formatDateTimeLocal, formatEur, formatKwh, formatSntKwh } from '../utils/formatters'
import { sanitizeHtml } from '../utils/sanitizeHtml'
import { useTranslation } from 'react-i18next'
import { useToast } from '../components/shared/Toast'
import { Modal } from '../components/shared/Modal'
import { EmailTemplateEditor } from '../components/admin/EmailTemplateEditor'
import { ActivationWizard } from '../components/admin/ActivationWizard'

type Tab = 'users' | 'contracts' | 'sessions' | 'pricing' | 'stats' | 'reports' | 'parking' | 'auditLog' | 'settings' | 'templates' | 'system' | 'releasenotes'

const VALID_TABS: Tab[] = ['users', 'contracts', 'sessions', 'pricing', 'stats', 'reports', 'parking', 'auditLog', 'settings', 'templates', 'system', 'releasenotes']

export function AdminPage() {
  const { t } = useTranslation()
  const [searchParams, setSearchParams] = useSearchParams()
  const tabParam = searchParams.get('tab') as Tab | null
  const tab: Tab = tabParam && VALID_TABS.includes(tabParam) ? tabParam : 'users'
  const setTab = (t: Tab) => setSearchParams({ tab: t }, { replace: true })

  const tabGroups: { id: Tab; label: string }[][] = [
    [
      { id: 'users', label: t('admin.tabs.users') },
      { id: 'contracts', label: t('admin.tabs.contracts') },
      { id: 'sessions', label: t('admin.tabs.sessions') },
      { id: 'stats', label: t('admin.tabs.stats') },
      { id: 'reports', label: t('admin.tabs.reports') },
    ],
    [
      { id: 'parking', label: t('admin.tabs.parking') },
      { id: 'pricing', label: t('admin.tabs.pricing') },
      { id: 'auditLog', label: t('admin.tabs.auditLog') },
      { id: 'settings', label: t('admin.tabs.settings') },
      { id: 'templates', label: t('admin.templates.tab') },
      { id: 'system', label: t('admin.tabs.system') },
      { id: 'releasenotes', label: t('admin.tabs.releasenotes') },
    ],
  ]

  const tabClass = (id: Tab) =>
    `px-3 py-1.5 text-sm font-medium transition-colors rounded-lg whitespace-nowrap
    ${tab === id ? 'bg-brand-500 text-white' : 'text-brand-500 hover:bg-brand-50'}`

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-bold text-brand-700">{t('admin.title')}</h1>

      <div className="space-y-1">
        {tabGroups.map((group, gi) => (
          <div key={gi} className="flex flex-wrap gap-1">
            {group.map(tb => (
              <button key={tb.id} onClick={() => setTab(tb.id)} className={tabClass(tb.id)}>
                {tb.label}
              </button>
            ))}
          </div>
        ))}
      </div>

      {tab === 'users' && <UsersTab />}
      {tab === 'contracts' && <ContractsTab />}
      {tab === 'sessions' && <AdminSessionsTab />}
      {tab === 'pricing' && <PricingTab />}
      {tab === 'stats' && <StatsTab />}
      {tab === 'reports' && <ReportsTab />}
      {tab === 'parking' && <ParkingTab />}
      {tab === 'auditLog' && <AuditLogTab />}
      {tab === 'settings' && <SettingsTab />}
      {tab === 'templates' && <EmailTemplateEditor />}
      {tab === 'system' && <SystemStatusTab />}
      {tab === 'releasenotes' && <ReleaseNotesTab />}
    </div>
  )
}

// --- Users Tab ---
interface User {
  rowKey: string
  email: string
  displayName: string
  apartmentNumber: string
  parkingSpaceNumber?: string
  phone: string
  status: string
  is_user: boolean
  is_admin: boolean
  createdAt: string
  pendingType: 'registration' | 'subuser' | null
  contract: {
    contractId: string
    parkingNumber: string
    role: 'primary' | 'subuser'
    primaryHolderName?: string
  } | null
}

function UsersTab() {
  const { t } = useTranslation()
  const { showToast } = useToast()
  const [users, setUsers] = useState<User[]>([])
  const [loading, setLoading] = useState(true)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editForm, setEditForm] = useState<Partial<User>>({})
  const [saving, setSaving] = useState(false)
  const [wizardUser, setWizardUser] = useState<User | null>(null)
  const [showCreateUser, setShowCreateUser] = useState(false)
  const [createUserForm, setCreateUserForm] = useState({
    displayName: '', email: '', phone: '', apartmentNumber: '',
    is_user: true, is_admin: false, createContract: true,
  })
  const [creatingUser, setCreatingUser] = useState(false)
  const [editSubUser, setEditSubUser] = useState<User | null>(null)
  const [editSubUserForm, setEditSubUserForm] = useState({ displayName: '', phone: '', status: '' })
  const [savingSubUser, setSavingSubUser] = useState(false)
  const [showCreateSubUser, setShowCreateSubUser] = useState(false)
  const [createSubUserForm, setCreateSubUserForm] = useState({
    primaryUserId: '', displayName: '', email: '', phone: '',
  })
  const [creatingSubUser, setCreatingSubUser] = useState(false)
  const [userSearch, setUserSearch] = useState('')

  interface PendingSubUser {
    email: string
    displayName: string
    contractId: string
    primaryHolderName: string
    apartmentNumber: string
    parkingNumber: string
  }
  const [pendingSubUsers, setPendingSubUsers] = useState<PendingSubUser[]>([])

  const load = useCallback(() => {
    setLoading(true)
    apiClient.get<{ items: User[]; pendingSubUsers: PendingSubUser[] }>('/api/manage/users')
      .then(r => {
        setUsers(r.items)
        setPendingSubUsers(r.pendingSubUsers ?? [])
      })
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => { load() }, [load])

  async function activateSubUserDirect(contractId: string, email: string) {
    try {
      await apiClient.post(`/api/manage/contracts/${contractId}/subusers/${email}/activate`)
      showToast(t('admin.subUsers.activated'), 'success')
      load()
    } catch {
      showToast(t('errors.generic'), 'error')
    }
  }

  async function handleCreateUser() {
    setCreatingUser(true)
    try {
      const { createContract, ...userData } = createUserForm
      const res = await apiClient.post<{ userId: string }>('/api/manage/users', userData)

      if (createContract && userData.is_user) {
        // Open wizard for the newly created user to set up contract
        const newUser: User = {
          rowKey: res.userId,
          email: userData.email,
          displayName: userData.displayName,
          apartmentNumber: userData.apartmentNumber,
          phone: userData.phone,
          status: 'active',
          is_user: userData.is_user,
          is_admin: userData.is_admin,
          createdAt: new Date().toISOString(),
          pendingType: null,
          contract: null,
        }
        setShowCreateUser(false)
        setWizardUser(newUser)
      } else {
        showToast(t('admin.users.userCreated'), 'success')
        setShowCreateUser(false)
        load()
      }
    } catch (err: unknown) {
      const status = (err as { status?: number }).status
      if (status === 409) {
        showToast(t('errors.emailAlreadyRegistered'), 'error')
      } else {
        showToast(t('errors.generic'), 'error')
      }
    } finally {
      setCreatingUser(false)
      setCreateUserForm({ displayName: '', email: '', phone: '', apartmentNumber: '', is_user: true, is_admin: false, createContract: true })
    }
  }

  function startEditSubUser(u: User) {
    setEditSubUser(u)
    setEditSubUserForm({ displayName: u.displayName, phone: u.phone, status: u.status })
  }

  async function saveSubUser() {
    if (!editSubUser?.contract?.contractId) return
    setSavingSubUser(true)
    try {
      await apiClient.put(`/api/manage/contracts/${editSubUser.contract.contractId}/subusers/${editSubUser.email}`, editSubUserForm)
      showToast(t('subUsers.updated'), 'success')
      setEditSubUser(null)
      load()
    } catch {
      showToast(t('errors.generic'), 'error')
    } finally {
      setSavingSubUser(false)
    }
  }

  async function handleCreateSubUser() {
    setCreatingSubUser(true)
    try {
      // Find primary user's active contract to use admin createSubUser endpoint
      const contractsRes = await apiClient.get<{ items: { rowKey: string; userId: string; parentContractId: string | null; status: string }[] }>('/api/manage/contracts')
      const primaryContract = contractsRes.items.find(c => c.userId === createSubUserForm.primaryUserId && !c.parentContractId && c.status === 'active')
      if (!primaryContract) {
        showToast(t('admin.users.noPrimaryContract'), 'error')
        return
      }
      await apiClient.post(`/api/manage/contracts/${primaryContract.rowKey}/subusers`, {
        email: createSubUserForm.email,
        displayName: createSubUserForm.displayName,
        phone: createSubUserForm.phone,
      })
      showToast(t('admin.subUsers.added'), 'success')
      setShowCreateSubUser(false)
      load()
    } catch {
      showToast(t('errors.generic'), 'error')
    } finally {
      setCreatingSubUser(false)
      setCreateSubUserForm({ primaryUserId: '', displayName: '', email: '', phone: '' })
    }
  }

  function startEdit(u: User) {
    setEditingId(u.rowKey)
    setEditForm({
      displayName: u.displayName,
      apartmentNumber: u.apartmentNumber,
      phone: u.phone,
      is_user: u.is_user,
      is_admin: u.is_admin,
      status: u.status,
    })
  }

  function cancelEdit() {
    setEditingId(null)
    setEditForm({})
  }

  function editFormErrors(): string | null {
    if (!editForm.displayName?.trim()) return t('admin.users.errorNameRequired')
    if (!editForm.apartmentNumber?.trim()) return t('admin.users.errorApartmentRequired')
    if (!editForm.phone?.trim()) return t('admin.users.errorPhoneRequired')
    return null
  }

  async function saveEdit(userId: string) {
    const err = editFormErrors()
    if (err) { showToast(err, 'error'); return }
    setSaving(true)
    try {
      await apiClient.put(`/api/manage/users/${userId}`, editForm)
      showToast(t('admin.users.userSaved'), 'success')
      setEditingId(null)
      setEditForm({})
      load()
    } catch (e) {
      showToast((e as Error).message || t('errors.generic'), 'error')
    } finally {
      setSaving(false)
    }
  }

  if (loading) return <p className="text-brand-400 py-4">{t('admin.loading')}</p>

  const pending = users.filter(u => u.status === 'pending')
  const searchLower = userSearch.toLowerCase()
  const active = users.filter(u => {
    if (u.status === 'pending' || u.status === 'rejected') return false
    if (searchLower) {
      const s = (v: unknown) => String(v ?? '').toLowerCase()
      return s(u.displayName).includes(searchLower)
        || s(u.email).includes(searchLower)
        || s(u.apartmentNumber).includes(searchLower)
        || s(u.phone).includes(searchLower)
        || s(u.contract?.parkingNumber).includes(searchLower)
    }
    return true
  })

  return (
    <div className="space-y-4">
      {(pending.length > 0 || pendingSubUsers.length > 0) && (
        <div>
          <h3 className="text-sm font-semibold text-amber-700 mb-2">
            {t('admin.users.pendingTitle')} ({pending.length + pendingSubUsers.length})
          </h3>
          <div className="space-y-2">
            {/* Pending registrations */}
            {pending.map(u => (
              <div key={u.rowKey} className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex flex-col sm:flex-row sm:items-center gap-2 sm:justify-between">
                <div>
                  <div className="flex items-center gap-2 mb-0.5">
                    <p className="font-medium text-brand-700">{u.displayName}</p>
                    <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">{t('admin.users.newRegistration')}</span>
                  </div>
                  <p className="text-sm text-brand-500">
                    {u.email} · {u.phone} · {t('admin.users.apartment')} {u.apartmentNumber}
                    {u.parkingSpaceNumber && ` · ${t('register.parkingSpace')} ${u.parkingSpaceNumber}`}
                  </p>
                </div>
                <button onClick={() => setWizardUser(u)}
                  className="bg-brand-500 text-white text-sm px-4 py-1.5 rounded-lg hover:bg-brand-600 transition-colors whitespace-nowrap">
                  {t('admin.wizard.title')}
                </button>
              </div>
            ))}
            {/* Pending sub-user requests */}
            {pendingSubUsers.map(su => (
              <div key={`sub-${su.contractId}-${su.email}`} className="bg-purple-50 border border-purple-200 rounded-lg p-3 flex flex-col sm:flex-row sm:items-center gap-2 sm:justify-between">
                <div>
                  <div className="flex items-center gap-2 mb-0.5">
                    <p className="font-medium text-brand-700">{su.displayName}</p>
                    <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded">{t('admin.users.subUserRequest')}</span>
                  </div>
                  <p className="text-sm text-brand-500">
                    {su.email} · {t('admin.users.subUserOf')} {su.primaryHolderName}
                    {' · P'}{su.parkingNumber} · {t('admin.users.apartment')} {su.apartmentNumber}
                  </p>
                </div>
                <button onClick={() => activateSubUserDirect(su.contractId, su.email)}
                  className="bg-green-600 text-white text-sm px-4 py-1.5 rounded-lg hover:bg-green-700 transition-colors whitespace-nowrap">
                  {t('admin.users.activate')}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {wizardUser && (
        <ActivationWizard
          user={wizardUser}
          onClose={() => setWizardUser(null)}
          onComplete={() => { setWizardUser(null); load() }}
        />
      )}

      {/* Edit modal */}
      <Modal open={!!editingId} onClose={cancelEdit} title={t('admin.users.edit')}>
        <div className="space-y-4">
          <Field label={t('admin.users.name') + ' *'}>
            <input type="text" required value={editForm.displayName ?? ''} onChange={e => setEditForm(f => ({ ...f, displayName: e.target.value }))}
              className="w-full rounded-lg border-brand-100 text-sm focus:border-brand-500 focus:ring-brand-500" />
          </Field>
          <Field label={t('admin.users.email')}>
            <p className="text-sm text-brand-600 py-1">{users.find(u => u.rowKey === editingId)?.email}</p>
          </Field>
          <Field label={t('admin.users.apartment') + ' *'}>
            <input type="text" required value={editForm.apartmentNumber ?? ''} onChange={e => setEditForm(f => ({ ...f, apartmentNumber: e.target.value }))}
              placeholder={t('admin.users.placeholderDashIfNone')}
              className="w-full rounded-lg border-brand-100 text-sm focus:border-brand-500 focus:ring-brand-500" />
          </Field>
          <Field label={t('admin.users.phone') + ' *'}>
            <input type="text" required value={editForm.phone ?? ''} onChange={e => setEditForm(f => ({ ...f, phone: e.target.value }))}
              placeholder={t('admin.users.placeholderDashIfNone')}
              className="w-full rounded-lg border-brand-100 text-sm focus:border-brand-500 focus:ring-brand-500" />
          </Field>
          <div className="flex gap-6">
            <label className="inline-flex items-center gap-2 text-sm">
              <input type="checkbox" checked={editForm.is_user ?? false} onChange={e => setEditForm(f => ({ ...f, is_user: e.target.checked }))}
                className="rounded border-brand-300 text-brand-500 focus:ring-brand-500" />
              {t('admin.users.roleUser')}
            </label>
            <label className="inline-flex items-center gap-2 text-sm">
              <input type="checkbox" checked={editForm.is_admin ?? false} onChange={e => setEditForm(f => ({ ...f, is_admin: e.target.checked }))}
                className="rounded border-brand-300 text-brand-500 focus:ring-brand-500" />
              {t('admin.users.roleAdmin')}
            </label>
          </div>
          <Field label={t('admin.users.status')}>
            <select value={editForm.status ?? 'active'} onChange={e => setEditForm(f => ({ ...f, status: e.target.value }))}
              className="w-full rounded-lg border-brand-100 text-sm focus:border-brand-500 focus:ring-brand-500">
              <option value="active">active</option>
              <option value="inactive">inactive</option>
            </select>
          </Field>
          <div className="flex gap-3 pt-2">
            <button onClick={() => editingId && saveEdit(editingId)} disabled={saving || editFormErrors() !== null}
              className="bg-brand-500 text-white text-sm px-5 py-2 rounded-lg hover:bg-brand-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
              {saving ? t('admin.settings.saving') : t('admin.users.save')}
            </button>
            <button onClick={cancelEdit}
              className="text-brand-500 text-sm px-5 py-2 rounded-lg hover:bg-brand-50 transition-colors">
              {t('admin.users.cancel')}
            </button>
          </div>
        </div>
      </Modal>

      {/* Create user modal */}
      <Modal open={showCreateUser} onClose={() => setShowCreateUser(false)} title={t('admin.users.createUser')}>
        <div className="space-y-4">
          <Field label={t('admin.users.name')}>
            <input type="text" value={createUserForm.displayName} onChange={e => setCreateUserForm(f => ({ ...f, displayName: e.target.value }))}
              className="w-full rounded-lg border-brand-100 text-sm focus:border-brand-500 focus:ring-brand-500" />
          </Field>
          <Field label={t('admin.users.email')}>
            <input type="email" value={createUserForm.email} onChange={e => setCreateUserForm(f => ({ ...f, email: e.target.value }))}
              className="w-full rounded-lg border-brand-100 text-sm focus:border-brand-500 focus:ring-brand-500" />
          </Field>
          <Field label={t('admin.users.phone')}>
            <input type="tel" value={createUserForm.phone} onChange={e => setCreateUserForm(f => ({ ...f, phone: e.target.value }))}
              className="w-full rounded-lg border-brand-100 text-sm focus:border-brand-500 focus:ring-brand-500" />
          </Field>
          <Field label={t('admin.users.apartment')}>
            <input type="text" value={createUserForm.apartmentNumber} onChange={e => setCreateUserForm(f => ({ ...f, apartmentNumber: e.target.value }))}
              className="w-full rounded-lg border-brand-100 text-sm focus:border-brand-500 focus:ring-brand-500" />
          </Field>
          <div className="flex gap-6">
            <label className="inline-flex items-center gap-2 text-sm">
              <input type="checkbox" checked={createUserForm.is_user} onChange={e => setCreateUserForm(f => ({ ...f, is_user: e.target.checked }))}
                className="rounded border-brand-300 text-brand-500 focus:ring-brand-500" />
              {t('admin.users.roleUser')}
            </label>
            <label className="inline-flex items-center gap-2 text-sm">
              <input type="checkbox" checked={createUserForm.is_admin} onChange={e => setCreateUserForm(f => ({ ...f, is_admin: e.target.checked }))}
                className="rounded border-brand-300 text-brand-500 focus:ring-brand-500" />
              {t('admin.users.roleAdmin')}
            </label>
          </div>
          {createUserForm.is_user && (
            <label className="inline-flex items-center gap-2 text-sm">
              <input type="checkbox" checked={createUserForm.createContract} onChange={e => setCreateUserForm(f => ({ ...f, createContract: e.target.checked }))}
                className="rounded border-brand-300 text-brand-500 focus:ring-brand-500" />
              {t('admin.users.createContract')}
            </label>
          )}
          <div className="flex gap-3 pt-2">
            <button onClick={handleCreateUser} disabled={creatingUser || !createUserForm.displayName || !createUserForm.email}
              className="bg-brand-500 text-white text-sm px-5 py-2 rounded-lg hover:bg-brand-600 disabled:opacity-50 transition-colors">
              {t('admin.users.save')}
            </button>
            <button onClick={() => setShowCreateUser(false)}
              className="text-brand-500 text-sm px-5 py-2 rounded-lg hover:bg-brand-50 transition-colors">
              {t('admin.users.cancel')}
            </button>
          </div>
        </div>
      </Modal>

      {/* Edit sub-user modal */}
      <Modal open={!!editSubUser} onClose={() => setEditSubUser(null)} title={t('subUsers.edit')}>
        <div className="space-y-4">
          <Field label={t('admin.users.email')}>
            <p className="text-sm text-brand-600 py-1">{editSubUser?.email}</p>
          </Field>
          <Field label={t('admin.users.name')}>
            <input type="text" value={editSubUserForm.displayName} onChange={e => setEditSubUserForm(f => ({ ...f, displayName: e.target.value }))}
              className="w-full rounded-lg border-brand-100 text-sm focus:border-brand-500 focus:ring-brand-500" />
          </Field>
          <Field label={t('admin.users.phone')}>
            <input type="tel" value={editSubUserForm.phone} onChange={e => setEditSubUserForm(f => ({ ...f, phone: e.target.value }))}
              className="w-full rounded-lg border-brand-100 text-sm focus:border-brand-500 focus:ring-brand-500" />
          </Field>
          <Field label={t('admin.users.status')}>
            <select value={editSubUserForm.status} onChange={e => setEditSubUserForm(f => ({ ...f, status: e.target.value }))}
              className="w-full rounded-lg border-brand-100 text-sm focus:border-brand-500 focus:ring-brand-500">
              <option value="active">active</option>
              <option value="inactive">inactive</option>
            </select>
          </Field>
          <Field label={t('admin.users.primaryHolder')}>
            <p className="text-sm text-brand-600 py-1">{editSubUser?.contract?.primaryHolderName}</p>
          </Field>
          <div className="flex gap-3 pt-2">
            <button onClick={saveSubUser} disabled={savingSubUser}
              className="bg-brand-500 text-white text-sm px-5 py-2 rounded-lg hover:bg-brand-600 disabled:opacity-50 transition-colors">
              {savingSubUser ? t('admin.settings.saving') : t('admin.users.save')}
            </button>
            <button onClick={() => setEditSubUser(null)}
              className="text-brand-500 text-sm px-5 py-2 rounded-lg hover:bg-brand-50 transition-colors">
              {t('admin.users.cancel')}
            </button>
          </div>
        </div>
      </Modal>

      {/* Create sub-user modal */}
      <Modal open={showCreateSubUser} onClose={() => setShowCreateSubUser(false)} title={t('admin.users.createSubUser')}>
        <div className="space-y-4">
          <Field label={t('admin.users.primaryHolder')}>
            <select value={createSubUserForm.primaryUserId} onChange={e => setCreateSubUserForm(f => ({ ...f, primaryUserId: e.target.value }))}
              className="w-full rounded-lg border-brand-100 text-sm focus:border-brand-500 focus:ring-brand-500">
              <option value="">--</option>
              {active.filter(u => u.contract?.role === 'primary').map(u => (
                <option key={u.rowKey} value={u.rowKey}>{u.displayName} ({u.email})</option>
              ))}
            </select>
          </Field>
          <Field label={t('admin.users.name')}>
            <input type="text" value={createSubUserForm.displayName} onChange={e => setCreateSubUserForm(f => ({ ...f, displayName: e.target.value }))}
              className="w-full rounded-lg border-brand-100 text-sm focus:border-brand-500 focus:ring-brand-500" />
          </Field>
          <Field label={t('admin.users.email')}>
            <input type="email" value={createSubUserForm.email} onChange={e => setCreateSubUserForm(f => ({ ...f, email: e.target.value }))}
              className="w-full rounded-lg border-brand-100 text-sm focus:border-brand-500 focus:ring-brand-500" />
          </Field>
          <Field label={t('admin.users.phone')}>
            <input type="tel" value={createSubUserForm.phone} onChange={e => setCreateSubUserForm(f => ({ ...f, phone: e.target.value }))}
              className="w-full rounded-lg border-brand-100 text-sm focus:border-brand-500 focus:ring-brand-500" />
          </Field>
          <div className="flex gap-3 pt-2">
            <button onClick={handleCreateSubUser}
              disabled={creatingSubUser || !createSubUserForm.primaryUserId || !createSubUserForm.displayName || !createSubUserForm.email}
              className="bg-brand-500 text-white text-sm px-5 py-2 rounded-lg hover:bg-brand-600 disabled:opacity-50 transition-colors">
              {t('admin.users.save')}
            </button>
            <button onClick={() => setShowCreateSubUser(false)}
              className="text-brand-500 text-sm px-5 py-2 rounded-lg hover:bg-brand-50 transition-colors">
              {t('admin.users.cancel')}
            </button>
          </div>
        </div>
      </Modal>

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-brand-600">{t('admin.users.activeTitle')} ({active.length})</h3>
          <div className="flex gap-2">
            <button onClick={() => setShowCreateSubUser(true)}
            className="text-brand-500 border border-brand-200 text-sm px-4 py-1.5 rounded-lg hover:bg-brand-50 transition-colors">
            {t('admin.users.createSubUser')}
          </button>
          <button onClick={() => setShowCreateUser(true)}
            className="bg-brand-500 text-white text-sm px-4 py-1.5 rounded-lg hover:bg-brand-600 transition-colors">
            {t('admin.users.createUser')}
          </button>
          </div>
        </div>
        <input type="text" value={userSearch} onChange={e => setUserSearch(e.target.value)}
          placeholder={t('admin.users.searchPlaceholder')}
          className="w-full rounded-lg border-brand-100 text-sm focus:border-brand-500 focus:ring-brand-500" />
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-brand-500 text-xs border-b border-brand-100">
              <th className="pb-2">{t('admin.users.name')}</th>
              <th className="pb-2">{t('admin.users.email')}</th>
              <th className="pb-2">{t('admin.users.apartment')}</th>
              <th className="pb-2">{t('admin.users.contractRole')}</th>
              <th className="pb-2">{t('admin.users.roles')}</th>
              <th className="pb-2">{t('admin.users.status')}</th>
              <th className="pb-2"></th>
            </tr>
          </thead>
          <tbody>
            {active.map(u => (
                <tr key={u.rowKey} className="border-b border-brand-50">
                  <td className="py-2 font-medium text-brand-700">{u.displayName}</td>
                  <td className="py-2 text-brand-600 text-xs">{u.email}</td>
                  <td className="py-2">{u.apartmentNumber}</td>
                  <td className="py-2">
                    {u.contract ? (
                      u.contract.role === 'primary' ? (
                        <span className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded">
                          {t('admin.users.primaryHolder')} · P{u.contract.parkingNumber}
                        </span>
                      ) : (
                        <span className="text-xs bg-purple-50 text-purple-700 px-2 py-0.5 rounded">
                          {t('admin.users.subUserOf')} {u.contract.primaryHolderName}
                          {u.contract.parkingNumber && ` · P${u.contract.parkingNumber}`}
                        </span>
                      )
                    ) : (
                      <span className="text-xs text-brand-400">—</span>
                    )}
                  </td>
                  <td className="py-2 space-x-1">
                    {u.is_user && <span className="text-xs bg-brand-50 text-brand-600 px-2 py-0.5 rounded">{t('admin.users.roleUser')}</span>}
                    {u.is_admin && <span className="text-xs bg-brand-100 text-brand-700 px-2 py-0.5 rounded font-medium">{t('admin.users.roleAdmin')}</span>}
                  </td>
                  <td className="py-2">
                    <span className={`text-xs px-2 py-0.5 rounded ${u.status === 'active' ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                      {u.status}
                    </span>
                  </td>
                  <td className="py-2">
                    <button onClick={() => u.contract?.role === 'subuser' ? startEditSubUser(u) : startEdit(u)}
                      className="text-brand-500 text-xs px-3 py-1 rounded hover:bg-brand-50 hover:text-brand-700 transition-colors">
                      {t('admin.users.edit')}
                    </button>
                  </td>
                </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// --- Contracts Tab (with sub-users) ---
interface Contract {
  rowKey: string
  userId: string
  parkingSpaceId: string
  apartmentNumber: string
  parentContractId: string | null
  status: string
  startDate: string
  endDate: string | null
  userName: string
  parkingNumber: string
}

interface ParkingSpace {
  rowKey: string
  name: string
  label?: string
}



function ContractsTab() {
  const { t } = useTranslation()
  const { showToast } = useToast()
  const [contracts, setContracts] = useState<Contract[]>([])
  const [loading, setLoading] = useState(true)
  const [showCreate, setShowCreate] = useState(false)
  const [users, setUsers] = useState<User[]>([])
  const [parkingSpaces, setParkingSpaces] = useState<ParkingSpace[]>([])
  const [createForm, setCreateForm] = useState({ userId: '', parkingSpaceId: '', apartmentNumber: '', startDate: '' })
  const [creating, setCreating] = useState(false)
  const [ending, setEnding] = useState<string | null>(null)
  const [approvingContract, setApprovingContract] = useState<Contract | null>(null)
  const [approveForm, setApproveForm] = useState({ parkingSpaceId: '', startDate: '' })
  const [approving, setApproving] = useState(false)
  const [editingContract, setEditingContract] = useState<Contract | null>(null)
  const [editContractForm, setEditContractForm] = useState({ apartmentNumber: '', startDate: '', parkingSpaceId: '' })
  const [savingContract, setSavingContract] = useState(false)

  const loadContracts = useCallback(() => {
    setLoading(true)
    Promise.all([
      apiClient.get<{ items: Contract[] }>('/api/manage/contracts'),
      apiClient.get<{ items: { rowKey: string; spaceNumber: string }[] }>('/api/manage/parking'),
    ]).then(([c, p]) => {
      setContracts(c.items)
      setParkingSpaces(p.items.map(s => ({ rowKey: s.rowKey, name: s.spaceNumber })))
    }).finally(() => setLoading(false))
  }, [])

  useEffect(() => { loadContracts() }, [loadContracts])

  function startEditContract(c: Contract) {
    setEditingContract(c)
    setEditContractForm({ apartmentNumber: c.apartmentNumber, startDate: c.startDate, parkingSpaceId: c.parkingSpaceId })
  }

  async function saveContract() {
    if (!editingContract) return
    setSavingContract(true)
    try {
      await apiClient.put(`/api/manage/contracts/${editingContract.rowKey}`, editContractForm)
      showToast(t('admin.contracts.saved'), 'success')
      setEditingContract(null)
      loadContracts()
    } catch {
      showToast(t('errors.generic'), 'error')
    } finally {
      setSavingContract(false)
    }
  }

  async function openCreateForm() {
    const [usersRes, parkingRes] = await Promise.all([
      apiClient.get<{ items: User[] }>('/api/manage/users'),
      apiClient.get<{ items: ParkingSpace[] }>('/api/manage/parking'),
    ])
    setUsers(usersRes.items.filter(u => u.status === 'active'))
    setParkingSpaces(parkingRes.items.map((s) => ({ rowKey: s.rowKey, name: (s as unknown as { spaceNumber: string }).spaceNumber || s.name })))
    setCreateForm({ userId: '', parkingSpaceId: '', apartmentNumber: '', startDate: '' })
    setShowCreate(true)
  }

  async function submitCreate() {
    setCreating(true)
    try {
      await apiClient.post('/api/manage/contracts', createForm)
      showToast(t('admin.contracts.created'), 'success')
      setShowCreate(false)
      loadContracts()
    } catch {
      showToast(t('errors.generic'), 'error')
    } finally {
      setCreating(false)
    }
  }

  function openApprove(c: Contract) {
    const requestedNumber = (c as unknown as { parkingSpaceNumber?: string }).parkingSpaceNumber ?? ''
    const matchedSpace = parkingSpaces.find(p => String(p.name) === requestedNumber)
    setApprovingContract(c)
    setApproveForm({
      parkingSpaceId: matchedSpace?.rowKey ?? '',
      startDate: `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}-01`,
    })
  }

  async function submitApprove() {
    if (!approvingContract) return
    setApproving(true)
    try {
      await apiClient.post(`/api/manage/contracts/${approvingContract.rowKey}/approve`, approveForm)
      showToast(t('admin.contracts.approved'), 'success')
      setApprovingContract(null)
      loadContracts()
    } catch {
      showToast(t('errors.generic'), 'error')
    } finally {
      setApproving(false)
    }
  }

  async function endContract(contractId: string) {
    if (!window.confirm(t('admin.contracts.endConfirm'))) return
    setEnding(contractId)
    try {
      await apiClient.post(`/api/manage/contracts/${contractId}/end`)
      showToast(t('admin.contracts.ended'), 'success')
      loadContracts()
    } catch {
      showToast(t('errors.generic'), 'error')
    } finally {
      setEnding(null)
    }
  }

  if (loading) return <p className="text-brand-400 py-4">{t('admin.loading')}</p>

  const pending = contracts.filter(c => c.status === 'pending')
  const active = contracts.filter(c => c.status === 'active')
  const ended = contracts.filter(c => c.status === 'ended')

  return (
    <div className="space-y-4">
      {/* Pending contract requests */}
      {pending.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-amber-700 mb-2">{t('contracts.pendingApproval')} ({pending.length})</h3>
          <div className="space-y-2">
            {pending.map(c => (
              <div key={c.rowKey} className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex flex-col sm:flex-row sm:items-center gap-2 sm:justify-between">
                <div>
                  <p className="font-medium text-brand-700">
                    {c.parentContractId ? t('admin.contracts.subContract') : t('admin.contracts.primaryContract')}
                    {' · '}{c.userName}
                  </p>
                  <p className="text-sm text-brand-500">
                    {t('contracts.requestParking')}: {(c as unknown as { parkingSpaceNumber?: string }).parkingSpaceNumber || '-'}
                    {' · '}{t('admin.contracts.apartment')} {c.apartmentNumber}
                  </p>
                </div>
                <button onClick={() => openApprove(c)}
                  className="bg-green-600 text-white text-sm px-4 py-1.5 rounded-lg hover:bg-green-700 transition-colors whitespace-nowrap">
                  {t('admin.contracts.approve')}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Approve contract modal */}
      <Modal open={!!approvingContract} onClose={() => setApprovingContract(null)} title={t('admin.contracts.approve')}>
        <div className="space-y-4">
          <div className="bg-brand-50 rounded-lg p-3 text-sm space-y-1">
            <p><strong>{approvingContract?.userName}</strong></p>
            <p>{t('contracts.requestParking')}: {(approvingContract as unknown as { parkingSpaceNumber?: string })?.parkingSpaceNumber || '-'}</p>
            <p>{t('admin.contracts.apartment')}: {approvingContract?.apartmentNumber}</p>
          </div>
          <Field label={t('admin.contracts.parkingSpace')}>
            <select value={approveForm.parkingSpaceId} onChange={e => setApproveForm(f => ({ ...f, parkingSpaceId: e.target.value }))}
              className="w-full rounded-lg border-brand-100 text-sm focus:border-brand-500 focus:ring-brand-500">
              <option value="">--</option>
              {parkingSpaces.map(p => <option key={p.rowKey} value={p.rowKey}>{p.name}</option>)}
            </select>
          </Field>
          <Field label={t('admin.contracts.startDate')}>
            <input type="date" value={approveForm.startDate} onChange={e => setApproveForm(f => ({ ...f, startDate: e.target.value }))}
              className="w-full rounded-lg border-brand-100 text-sm focus:border-brand-500 focus:ring-brand-500" />
          </Field>
          <div className="flex gap-3 pt-2">
            <button onClick={submitApprove} disabled={approving || !approveForm.parkingSpaceId || !approveForm.startDate}
              className="bg-green-600 text-white text-sm px-5 py-2 rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors">
              {approving ? t('admin.settings.saving') : t('admin.contracts.approve')}
            </button>
            <button onClick={() => setApprovingContract(null)}
              className="text-brand-500 text-sm px-5 py-2 rounded-lg hover:bg-brand-50 transition-colors">
              {t('admin.users.cancel')}
            </button>
          </div>
        </div>
      </Modal>

      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-brand-600">{t('admin.contracts.activeTitle')} ({active.length})</h3>
        <button onClick={openCreateForm}
          className="bg-brand-500 text-white text-sm px-4 py-1.5 rounded-lg hover:bg-brand-600 transition-colors">
          {t('admin.contracts.create')}
        </button>
      </div>

      {showCreate && (
        <div className="bg-brand-50 border border-brand-200 rounded-lg p-4 space-y-3">
          <div>
            <label className="block text-xs text-brand-500 mb-1">{t('admin.contracts.user')}</label>
            <select value={createForm.userId} onChange={e => setCreateForm(f => ({ ...f, userId: e.target.value }))}
              className="w-full rounded-lg border-brand-200 text-sm focus:border-brand-500 focus:ring-brand-500">
              <option value="">--</option>
              {users.map(u => <option key={u.rowKey} value={u.rowKey}>{u.displayName} ({u.email})</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs text-brand-500 mb-1">{t('admin.contracts.parkingSpace')}</label>
            <select value={createForm.parkingSpaceId} onChange={e => setCreateForm(f => ({ ...f, parkingSpaceId: e.target.value }))}
              className="w-full rounded-lg border-brand-200 text-sm focus:border-brand-500 focus:ring-brand-500">
              <option value="">--</option>
              {parkingSpaces.map(p => <option key={p.rowKey} value={p.rowKey}>{p.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs text-brand-500 mb-1">{t('admin.users.apartment')}</label>
            <input type="text" value={createForm.apartmentNumber} onChange={e => setCreateForm(f => ({ ...f, apartmentNumber: e.target.value }))}
              className="w-full rounded-lg border-brand-200 text-sm focus:border-brand-500 focus:ring-brand-500" />
          </div>
          <div>
            <label className="block text-xs text-brand-500 mb-1">{t('admin.contracts.startDate')}</label>
            <input type="date" value={createForm.startDate} onChange={e => setCreateForm(f => ({ ...f, startDate: e.target.value }))}
              className="w-full rounded-lg border-brand-200 text-sm focus:border-brand-500 focus:ring-brand-500" />
          </div>
          <div className="flex gap-2">
            <button onClick={submitCreate} disabled={creating || !createForm.userId || !createForm.parkingSpaceId || !createForm.apartmentNumber || !createForm.startDate}
              className="bg-brand-500 text-white text-sm px-4 py-1.5 rounded-lg hover:bg-brand-600 disabled:opacity-50 transition-colors">
              {t('admin.users.save')}
            </button>
            <button onClick={() => setShowCreate(false)}
              className="text-brand-400 text-sm px-4 py-1.5 rounded-lg hover:text-brand-600 transition-colors">
              {t('admin.users.cancel')}
            </button>
          </div>
        </div>
      )}

      {/* Edit contract modal */}
      <Modal open={!!editingContract} onClose={() => setEditingContract(null)} title={t('admin.contracts.edit')}>
        <div className="space-y-4">
          <Field label={t('admin.contracts.parkingSpace')}>
            <select value={editContractForm.parkingSpaceId} onChange={e => setEditContractForm(f => ({ ...f, parkingSpaceId: e.target.value }))}
              className="w-full rounded-lg border-brand-100 text-sm focus:border-brand-500 focus:ring-brand-500">
              {parkingSpaces.map(p => <option key={p.rowKey} value={p.rowKey}>{p.name}</option>)}
            </select>
          </Field>
          <Field label={t('admin.contracts.apartment')}>
            <input type="text" value={editContractForm.apartmentNumber} onChange={e => setEditContractForm(f => ({ ...f, apartmentNumber: e.target.value }))}
              className="w-full rounded-lg border-brand-100 text-sm focus:border-brand-500 focus:ring-brand-500" />
          </Field>
          <Field label={t('admin.contracts.startDate')}>
            <input type="date" value={editContractForm.startDate} onChange={e => setEditContractForm(f => ({ ...f, startDate: e.target.value }))}
              className="w-full rounded-lg border-brand-100 text-sm focus:border-brand-500 focus:ring-brand-500" />
          </Field>
          <div className="flex gap-3 pt-2">
            <button onClick={saveContract} disabled={savingContract}
              className="bg-brand-500 text-white text-sm px-5 py-2 rounded-lg hover:bg-brand-600 disabled:opacity-50 transition-colors">
              {t('admin.users.save')}
            </button>
            <button onClick={() => setEditingContract(null)}
              className="text-brand-500 text-sm px-5 py-2 rounded-lg hover:bg-brand-50 transition-colors">
              {t('admin.users.cancel')}
            </button>
          </div>
        </div>
      </Modal>

      <div className="space-y-2">
        {active.map(c => (
          <div key={c.rowKey} className="bg-white border border-brand-100 rounded-lg p-3 flex justify-between items-start">
              <div>
                <p className="text-sm font-medium text-brand-700">
                  {c.parentContractId ? t('admin.contracts.subContract') : t('admin.contracts.primaryContract')}
                  {' · '}{c.userName}
                  {' · P'}{c.parkingNumber}
                  {' · '}{t('admin.contracts.apartment')} {c.apartmentNumber}
                </p>
                <p className="text-xs text-brand-400">{t('admin.contracts.startDate')} {c.startDate}</p>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs bg-green-50 text-green-700 px-2 py-0.5 rounded">{t('admin.contracts.active')}</span>
                <button onClick={() => startEditContract(c)}
                  className="text-brand-500 text-xs px-3 py-1 rounded hover:bg-brand-50 transition-colors">
                  {t('admin.users.edit')}
                </button>
                <button onClick={() => endContract(c.rowKey)} disabled={ending === c.rowKey}
                  className="text-red-500 text-xs px-3 py-1 rounded hover:bg-red-50 hover:text-red-700 disabled:opacity-50 transition-colors">
                  {t('admin.contracts.end')}
                </button>
              </div>
          </div>
        ))}
      </div>

      {ended.length > 0 && (
        <>
          <h3 className="text-sm font-semibold text-brand-400 mt-4">{t('admin.contracts.endedTitle')} ({ended.length})</h3>
          <div className="space-y-1 opacity-60">
            {ended.map(c => (
              <div key={c.rowKey} className="text-sm text-brand-500">
                {c.apartmentNumber} · {c.startDate} — {c.endDate}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  )
}

// --- Admin Sessions Tab ---
interface AdminInterval {
  startTimeUtc: string
  endTimeUtc: string
  energyKwh: number
  spotPriceSntKwh: number
  marginSntKwh: number
  intervalCostEur: number
}

interface AdminSession {
  startTime: string
  endTime: string
  totalEnergyKwh: number
  totalCostEur: number
  chargerName: string
  costCalculated: boolean
  priceEstimated: boolean
  partitionKey: string
  zaptecSessionId: string
  isUnlinked?: boolean
  userEmail?: string
  tokenName?: string
  displayName?: string
  intervals?: string
}

function parseAdminIntervals(json?: string): AdminInterval[] {
  if (!json) return []
  try { return JSON.parse(json) } catch { return [] }
}

function adminCostBreakdown(s: AdminSession) {
  const ivs = parseAdminIntervals(s.intervals)
  const spotEur = ivs.reduce((sum, iv) => sum + iv.energyKwh * iv.spotPriceSntKwh / 100, 0)
  const marginEur = ivs.reduce((sum, iv) => sum + iv.energyKwh * iv.marginSntKwh / 100, 0)
  return { spotEur, marginEur, intervals: ivs }
}

function localDateStr(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`
}
function adminCurrentDate() { return localDateStr(new Date()) }
function adminMonthAgo() { const d = new Date(); d.setDate(d.getDate() - 30); return localDateStr(d) }

function AdminSessionsTab() {
  const { t } = useTranslation()
  const { showToast } = useToast()
  const [sessions, setSessions] = useState<AdminSession[]>([])
  const [loading, setLoading] = useState(true)
  const [reassignLoading, setReassignLoading] = useState(false)
  const [syncLoading, setSyncLoading] = useState(false)
  const [syncFrom, setSyncFrom] = useState('')
  const [syncTo, setSyncTo] = useState('')
  const [showResync, setShowResync] = useState(false)
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [filterFrom, setFilterFrom] = useState(adminMonthAgo())
  const [filterTo, setFilterTo] = useState(adminCurrentDate())
  const [search, setSearch] = useState('')

  const reload = useCallback(() => {
    setLoading(true)
    apiClient.get<{ items: AdminSession[] }>('/api/manage/sessions')
      .then(r => setSessions(r.items))
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => { reload() }, [reload])

  if (loading) return <p className="text-brand-400 py-4">{t('admin.loading')}</p>

  const now = new Date()
  const presets = [
    { label: t('sessions.presetCurMonth'), fn: () => { setFilterFrom(localDateStr(new Date(now.getFullYear(), now.getMonth(), 1))); setFilterTo(adminCurrentDate()) } },
    { label: t('sessions.presetPrevMonth'), fn: () => { setFilterFrom(localDateStr(new Date(now.getFullYear(), now.getMonth()-1, 1))); setFilterTo(localDateStr(new Date(now.getFullYear(), now.getMonth(), 0))) } },
    { label: t('sessions.presetCurQuarter'), fn: () => { const q = Math.floor(now.getMonth()/3)*3; setFilterFrom(localDateStr(new Date(now.getFullYear(), q, 1))); setFilterTo(adminCurrentDate()) } },
    { label: t('sessions.presetLast30'), fn: () => { setFilterFrom(adminMonthAgo()); setFilterTo(adminCurrentDate()) } },
    { label: t('sessions.presetAll'), fn: () => { setFilterFrom(''); setFilterTo('') } },
  ]

  const searchLower = search.toLowerCase()
  const filtered = sessions
    .filter(s => {
      const endDate = (s.endTime || s.startTime).slice(0, 10)
      if (filterFrom && endDate < filterFrom) return false
      if (filterTo && endDate > filterTo) return false
      if (searchLower) {
        const match = (s.displayName?.toLowerCase().includes(searchLower))
          || (s.userEmail?.toLowerCase().includes(searchLower))
          || (s.tokenName?.toLowerCase().includes(searchLower))
          || (s.chargerName?.toLowerCase().includes(searchLower))
        if (!match) return false
      }
      return true
    })
    .sort((a, b) => (b.endTime || b.startTime).localeCompare(a.endTime || a.startTime))

  const unlinked = filtered.filter(s => s.isUnlinked)
  const linked = filtered.filter(s => !s.isUnlinked)
  const totalKwh = filtered.reduce((s, x) => s + x.totalEnergyKwh, 0)
  const totalEur = filtered.reduce((s, x) => s + x.totalCostEur, 0)

  async function handleReassignAll() {
    setReassignLoading(true)
    try {
      const res = await apiClient.post<{ reassigned: number; failed: number }>('/api/manage/sessions/reassign')
      showToast(t('admin.sessions.reassignResult', { reassigned: res.reassigned, failed: res.failed }), res.reassigned > 0 ? 'success' : 'warning')
      reload()
    } catch {
      showToast(t('errors.generic'), 'error')
    } finally {
      setReassignLoading(false)
    }
  }

  return (
    <div className="space-y-4">
      {/* Date filter + search */}
      <div className="space-y-2">
        <div className="flex flex-wrap gap-1">
          {presets.map(p => (
            <button key={p.label} onClick={p.fn}
              className="text-xs px-3 py-1 rounded-lg border border-brand-100 text-brand-600 hover:bg-brand-50 transition-colors">
              {p.label}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <input type="date" value={filterFrom} onChange={e => setFilterFrom(e.target.value)}
            className="rounded-lg border-brand-100 text-sm focus:border-brand-500 focus:ring-brand-500" />
          <span className="text-brand-400">—</span>
          <input type="date" value={filterTo} onChange={e => setFilterTo(e.target.value)}
            className="rounded-lg border-brand-100 text-sm focus:border-brand-500 focus:ring-brand-500" />
          <input type="text" value={search} onChange={e => setSearch(e.target.value)}
            placeholder={t('admin.sessions.searchPlaceholder')}
            className="rounded-lg border-brand-100 text-sm focus:border-brand-500 focus:ring-brand-500 flex-1 min-w-[200px]" />
        </div>
      </div>

      <div className="flex items-center gap-4 text-sm">
        <span className="text-brand-600">{sessions.length} {t('admin.sessions.count')}</span>
        <span className="text-brand-600">{formatKwh(totalKwh)}</span>
        <span className="font-semibold text-brand-700">{formatEur(totalEur)}</span>
        {unlinked.length > 0 && (
          <span className="text-amber-600 font-medium">{unlinked.length} {t('admin.sessions.unlinked')}</span>
        )}
        <div className="ml-auto flex items-center gap-2">
          <button onClick={handleReassignAll}
            disabled={unlinked.length === 0 || reassignLoading}
            className="bg-amber-500 hover:bg-amber-600 text-white text-xs font-medium px-3 py-1.5 rounded-lg
              disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
            {reassignLoading ? t('admin.loading') : t('admin.sessions.reassignAll')}
          </button>
          <button onClick={() => setShowResync(!showResync)}
            className="bg-brand-500 hover:bg-brand-600 text-white text-xs font-medium px-3 py-1.5 rounded-lg transition-colors">
            {t('admin.sessions.resync')}
          </button>
        </div>
      </div>

      {showResync && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-end gap-3">
          <div>
            <label className="block text-xs text-blue-700 mb-1">{t('admin.sessions.resyncFrom')}</label>
            <input type="date" value={syncFrom} onChange={e => setSyncFrom(e.target.value)}
              className="text-sm rounded border-blue-200 py-1" />
          </div>
          <div>
            <label className="block text-xs text-blue-700 mb-1">{t('admin.sessions.resyncTo')}</label>
            <input type="date" value={syncTo} onChange={e => setSyncTo(e.target.value)}
              className="text-sm rounded border-blue-200 py-1" />
          </div>
          <button onClick={async () => {
            if (!syncFrom) return
            setSyncLoading(true)
            try {
              const body: { from: string; to?: string } = { from: syncFrom }
              if (syncTo) body.to = syncTo
              const res = await apiClient.post<{ sessionsNew: number; sessionsSkipped: number }>('/api/manage/sync', body)
              showToast(t('admin.sessions.resyncResult', { count: res.sessionsNew }), 'success')
              setShowResync(false)
              reload()
            } catch { showToast(t('errors.generic'), 'error') }
            finally { setSyncLoading(false) }
          }}
            disabled={!syncFrom || syncLoading}
            className="bg-blue-500 hover:bg-blue-600 text-white text-xs font-medium px-3 py-1.5 rounded-lg
              disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
            {syncLoading ? t('admin.loading') : t('admin.sessions.resyncRun')}
          </button>
          <button onClick={() => setShowResync(false)} className="text-xs text-brand-500">{t('common.cancel')}</button>
        </div>
      )}

      {/* Unlinked sessions */}
      {unlinked.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-amber-800 mb-2">{t('admin.sessions.unlinkedTitle')}</h3>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-amber-700 text-xs border-b border-amber-200">
                <th className="pb-2">{t('admin.sessions.time')}</th>
                <th className="pb-2">{t('admin.sessions.email')}</th>
                <th className="pb-2">{t('admin.sessions.token')}</th>
                <th className="pb-2">{t('admin.sessions.device')}</th>
                <th className="pb-2 text-right">{t('admin.sessions.kwh')}</th>
              </tr>
            </thead>
            <tbody>
              {unlinked.map((s, i) => (
                <tr key={i} className="border-b border-amber-100">
                  <td className="py-1.5 text-brand-700">{formatDateTimeLocal(s.startTime)}</td>
                  <td className="py-1.5 font-medium text-amber-800">{s.userEmail ?? '?'}</td>
                  <td className="py-1.5 text-brand-500">{s.tokenName}</td>
                  <td className="py-1.5">{s.chargerName}</td>
                  <td className="py-1.5 text-right">{formatKwh(s.totalEnergyKwh)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Linked sessions */}
      <div className="space-y-2">
        {linked.slice(0, 50).map((s) => {
          const sid = s.zaptecSessionId
          const isOpen = expandedId === sid
          const { spotEur, marginEur, intervals } = adminCostBreakdown(s)

          return (
            <div key={sid} className="bg-white rounded-xl shadow-sm overflow-hidden">
              <button
                onClick={() => setExpandedId(isOpen ? null : sid)}
                className="w-full px-4 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 text-left hover:bg-brand-50 transition-colors"
              >
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-medium text-brand-700">{formatDateTimeLocal(s.endTime || s.startTime)}</span>
                  {s.displayName && <span className="text-xs font-medium text-brand-600">{s.displayName}</span>}
                  <span className="text-xs text-brand-400">{s.chargerName}</span>
                  {s.priceEstimated && (
                    <span className="text-xs text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded">{t('sessions.estimated')}</span>
                  )}
                </div>
                <div className="flex items-center gap-3 text-sm">
                  <span className="text-brand-600">{formatKwh(s.totalEnergyKwh)}</span>
                  {intervals.length > 0 && (
                    <>
                      <span className="text-brand-400 text-xs">{t('dashboard.spotLabel')} {formatEur(spotEur)}</span>
                      <span className="text-brand-400 text-xs">+ {t('dashboard.marginLabel')} {formatEur(marginEur)}</span>
                      <span className="text-brand-400">=</span>
                    </>
                  )}
                  <span className="text-brand-700 font-semibold">{formatEur(s.totalCostEur)}</span>
                  <span className="text-brand-400 text-xs">{isOpen ? '▲' : '▼'}</span>
                </div>
              </button>

              {isOpen && (
                <div className="border-t border-brand-50 px-4 py-4 space-y-4">
                  <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 text-sm">
                    <div>
                      <p className="text-brand-400 text-xs">{t('sessions.startTime')}</p>
                      <p className="font-medium text-brand-700">{formatDateTimeLocal(s.startTime)}</p>
                    </div>
                    <div>
                      <p className="text-brand-400 text-xs">{t('sessions.endTime')}</p>
                      <p className="font-medium text-brand-700">{formatDateTimeLocal(s.endTime)}</p>
                    </div>
                    <div>
                      <p className="text-brand-400 text-xs">{t('admin.sessions.email')}</p>
                      <p className="font-medium text-brand-700">{s.userEmail || '–'}</p>
                    </div>
                    <div>
                      <p className="text-brand-400 text-xs">{t('admin.sessions.token')}</p>
                      <p className="font-medium text-brand-700">{s.tokenName || t('sessions.zaptecApp')}</p>
                    </div>
                    <div>
                      <p className="text-brand-400 text-xs">{t('sessions.charger')}</p>
                      <p className="font-medium text-brand-700">{s.chargerName}</p>
                    </div>
                  </div>

                  {intervals.length > 0 && (
                    <>
                      <div className="bg-brand-50 rounded-lg p-3">
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                          <div>
                            <p className="text-brand-400 text-xs">{t('sessions.totalEnergy')}</p>
                            <p className="font-bold text-brand-700">{formatKwh(s.totalEnergyKwh)}</p>
                          </div>
                          <div>
                            <p className="text-brand-400 text-xs">{t('sessions.energyCost')}</p>
                            <p className="font-medium text-brand-600">{formatEur(spotEur)}</p>
                          </div>
                          <div>
                            <p className="text-brand-400 text-xs">{t('sessions.marginCost')}</p>
                            <p className="font-medium text-brand-600">{formatEur(marginEur)}</p>
                          </div>
                          <div>
                            <p className="text-brand-400 text-xs">{t('sessions.totalCost')}</p>
                            <p className="font-bold text-brand-700">{formatEur(s.totalCostEur)}</p>
                          </div>
                        </div>
                      </div>

                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="text-left text-brand-500 text-xs">
                              <th className="pb-1">{t('sessions.time')}</th>
                              <th className="pb-1 text-right">{t('sessions.kwh')}</th>
                              <th className="pb-1 text-right">{t('sessions.spot')}</th>
                              <th className="pb-1 text-right">{t('sessions.margin')}</th>
                              <th className="pb-1 text-right">{t('sessions.price')}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {intervals.map((iv, i) => (
                              <tr key={i} className="border-t border-brand-50">
                                <td className="py-1 text-brand-600">{formatDateTimeLocal(iv.startTimeUtc)}</td>
                                <td className="py-1 text-right">{formatKwh(iv.energyKwh)}</td>
                                <td className="py-1 text-right">{formatSntKwh(iv.spotPriceSntKwh)}</td>
                                <td className="py-1 text-right">{formatSntKwh(iv.marginSntKwh)}</td>
                                <td className="py-1 text-right font-medium">{formatEur(iv.intervalCostEur)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

// --- Pricing Tab ---
interface CostEntry {
  startDate: string
  endDate: string | null
  marginCents: number
  distributionCostSntKwh: number
  monthlyBaseFeeEur: number
  contractFeeEur: number
  setByUserId: string
  createdAt: string
}

function PricingTab() {
  const { t } = useTranslation()
  const { showToast } = useToast()
  const [entries, setEntries] = useState<CostEntry[]>([])
  const [loading, setLoading] = useState(true)
  const [showCreate, setShowCreate] = useState(false)
  const [form, setForm] = useState({
    startDate: new Date().toISOString().slice(0, 10),
    marginCents: 11,
    distributionCostSntKwh: 6.61697,
    monthlyBaseFeeEur: 42.285,
    contractFeeEur: 15,
  })
  const [saving, setSaving] = useState(false)

  const load = useCallback(() => {
    setLoading(true)
    apiClient.get<{ items: CostEntry[] }>('/api/manage/cost-schedule')
      .then(r => setEntries(r.items))
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => { load() }, [load])

  async function submitCreate() {
    setSaving(true)
    try {
      await apiClient.post('/api/manage/cost-schedule', form)
      showToast(t('admin.pricing.saved'), 'success')
      setShowCreate(false)
      load()
    } catch {
      showToast(t('errors.generic'), 'error')
    } finally {
      setSaving(false)
    }
  }

  if (loading) return <p className="text-brand-400 py-4">{t('admin.loading')}</p>

  const today = new Date().toISOString().slice(0, 10)
  const current = entries.find(e => e.startDate <= today) ?? null
  const upcoming = entries.find(e => e.startDate > today) ?? null

  return (
    <div className="space-y-6">
      {/* Current pricing */}
      {current && (
        <div className="bg-white rounded-xl shadow-sm p-5">
          <h3 className="text-sm font-semibold text-brand-600 mb-3">{t('admin.pricing.current')}</h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
            <div className="bg-brand-50 rounded-lg p-3">
              <p className="text-brand-400 text-xs mb-1">{t('admin.pricing.margin')}</p>
              <p className="text-xl font-bold text-brand-700">{current.marginCents} <span className="text-sm font-normal">snt/kWh</span></p>
              <p className="text-xs text-brand-400">{t('admin.pricing.inclVat')}</p>
            </div>
            <div className="bg-brand-50 rounded-lg p-3">
              <p className="text-brand-400 text-xs mb-1">{t('admin.pricing.distribution')}</p>
              <p className="text-xl font-bold text-brand-700">{current.distributionCostSntKwh} <span className="text-sm font-normal">snt/kWh</span></p>
              <p className="text-xs text-brand-400">{t('admin.pricing.inclVat')}</p>
            </div>
            <div className="bg-brand-50 rounded-lg p-3">
              <p className="text-brand-400 text-xs mb-1">{t('admin.pricing.baseFee')}</p>
              <p className="text-xl font-bold text-brand-700">{current.monthlyBaseFeeEur} <span className="text-sm font-normal">EUR/kk</span></p>
              <p className="text-xs text-brand-400">{t('admin.pricing.inclVat')}</p>
            </div>
            <div className="bg-green-50 rounded-lg p-3">
              <p className="text-brand-400 text-xs mb-1">{t('admin.pricing.contractFee')}</p>
              <p className="text-xl font-bold text-green-700">{current.contractFeeEur} <span className="text-sm font-normal">EUR/kk</span></p>
              <p className="text-xs text-brand-400">{t('admin.stats.contractRevenue')}</p>
            </div>
          </div>
          <p className="text-xs text-brand-400 mt-2">{t('admin.pricing.validFrom')} {current.startDate}</p>
        </div>
      )}

      {/* Upcoming pricing */}
      {upcoming && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
          <h3 className="text-sm font-semibold text-amber-700 mb-2">{t('admin.pricing.upcoming')} ({upcoming.startDate})</h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
            <div><span className="text-amber-600 text-xs">{t('admin.pricing.margin')}</span><br/><strong>{upcoming.marginCents}</strong> snt/kWh</div>
            <div><span className="text-amber-600 text-xs">{t('admin.pricing.distribution')}</span><br/><strong>{upcoming.distributionCostSntKwh}</strong> snt/kWh</div>
            <div><span className="text-amber-600 text-xs">{t('admin.pricing.baseFee')}</span><br/><strong>{upcoming.monthlyBaseFeeEur}</strong> EUR/kk</div>
            <div><span className="text-amber-600 text-xs">{t('admin.pricing.contractFee')}</span><br/><strong>{upcoming.contractFeeEur}</strong> EUR/kk</div>
          </div>
        </div>
      )}

      {/* Add new pricing */}
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-brand-600">{t('admin.pricing.history')}</h3>
        <button onClick={() => setShowCreate(true)}
          className="bg-brand-500 text-white text-sm px-4 py-1.5 rounded-lg hover:bg-brand-600 transition-colors">
          {t('admin.pricing.add')}
        </button>
      </div>

      <Modal open={showCreate} onClose={() => setShowCreate(false)} title={t('admin.pricing.add')}>
        <div className="space-y-4">
          <Field label={t('admin.pricing.validFrom')}>
            <input type="date" value={form.startDate} onChange={e => setForm(f => ({ ...f, startDate: e.target.value }))}
              className="w-full rounded-lg border-brand-100 text-sm focus:border-brand-500 focus:ring-brand-500" />
          </Field>
          <Field label={`${t('admin.pricing.margin')} (snt/kWh, ${t('admin.pricing.inclVat')})`}>
            <input type="number" step="0.01" value={form.marginCents} onChange={e => setForm(f => ({ ...f, marginCents: parseFloat(e.target.value) || 0 }))}
              className="w-full rounded-lg border-brand-100 text-sm focus:border-brand-500 focus:ring-brand-500" />
          </Field>
          <Field label={`${t('admin.pricing.distribution')} (snt/kWh, ${t('admin.pricing.inclVat')})`}>
            <input type="number" step="0.00001" value={form.distributionCostSntKwh} onChange={e => setForm(f => ({ ...f, distributionCostSntKwh: parseFloat(e.target.value) || 0 }))}
              className="w-full rounded-lg border-brand-100 text-sm focus:border-brand-500 focus:ring-brand-500" />
          </Field>
          <Field label={`${t('admin.pricing.baseFee')} (EUR/kk, ${t('admin.pricing.inclVat')})`}>
            <input type="number" step="0.001" value={form.monthlyBaseFeeEur} onChange={e => setForm(f => ({ ...f, monthlyBaseFeeEur: parseFloat(e.target.value) || 0 }))}
              className="w-full rounded-lg border-brand-100 text-sm focus:border-brand-500 focus:ring-brand-500" />
          </Field>
          <Field label={`${t('admin.pricing.contractFee')} (EUR/kk)`}>
            <input type="number" step="0.01" value={form.contractFeeEur} onChange={e => setForm(f => ({ ...f, contractFeeEur: parseFloat(e.target.value) || 0 }))}
              className="w-full rounded-lg border-brand-100 text-sm focus:border-brand-500 focus:ring-brand-500" />
          </Field>
          <div className="flex gap-3 pt-2">
            <button onClick={submitCreate} disabled={saving}
              className="bg-brand-500 text-white text-sm px-5 py-2 rounded-lg hover:bg-brand-600 disabled:opacity-50 transition-colors">
              {t('admin.settings.save')}
            </button>
            <button onClick={() => setShowCreate(false)}
              className="text-brand-500 text-sm px-5 py-2 rounded-lg hover:bg-brand-50 transition-colors">
              {t('admin.users.cancel')}
            </button>
          </div>
        </div>
      </Modal>

      {/* History table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-brand-500 text-xs border-b border-brand-100">
              <th className="pb-2">{t('admin.pricing.validFrom')}</th>
              <th className="pb-2 text-right">{t('admin.pricing.margin')}</th>
              <th className="pb-2 text-right">{t('admin.pricing.distribution')}</th>
              <th className="pb-2 text-right">{t('admin.pricing.baseFee')}</th>
              <th className="pb-2 text-right">{t('admin.pricing.contractFee')}</th>
            </tr>
          </thead>
          <tbody>
            {entries.map((e) => {
              const isCurrent = current && e.startDate === current.startDate
              const isUpcoming = e.startDate > today
              const rowBg = isCurrent ? 'bg-green-50/50' : isUpcoming ? 'bg-amber-50/50' : ''
              return (
              <tr key={e.startDate} className={`border-b border-brand-50 ${rowBg}`}>
                <td className="py-2 font-medium text-brand-700">{e.startDate}</td>
                <td className="py-2 text-right text-brand-600">{e.marginCents} snt/kWh</td>
                <td className="py-2 text-right text-brand-600">{e.distributionCostSntKwh} snt/kWh</td>
                <td className="py-2 text-right text-brand-600">{e.monthlyBaseFeeEur} EUR/kk</td>
                <td className="py-2 text-right text-green-600">{e.contractFeeEur} EUR/kk</td>
              </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// --- Statistics Tab ---
interface StatsData {
  totals: {
    sessions: number; kwh: number; eur: number; avgSpotSntKwh: number
    marginRevenue: number; contractRevenue: number; distributionCost: number; baseFee: number; totalCost: number; grossMargin: number
  }
  monthly: {
    month: string; sessions: number; kwh: number; eur: number; activeContracts: number
    marginRevenue: number; contractRevenue: number; distributionCost: number; baseFeeAllocation: number; totalCost: number; grossMargin: number
  }[]
  hasEstimated: boolean
  costParams: { marginCents: number; distributionCostSntKwh: number; monthlyBaseFeeEur: number; contractFeeEur: number; activeContracts: number }
}

interface ChargerStats {
  chargerId: string
  chargerName: string
  sessions: number
  kwh: number
  eur: number
}

interface ChargerStatsResponse {
  items: ChargerStats[]
  utilization: { used: number; total: number }
}

function currentMonth() { return new Date().toISOString().slice(0, 7) }

function StatsTab() {
  const { t } = useTranslation()
  const [stats, setStats] = useState<StatsData | null>(null)
  const [chargers, setChargers] = useState<ChargerStats[]>([])
  const [utilization, setUtilization] = useState<{ used: number; total: number }>({ used: 0, total: 0 })
  const [loading, setLoading] = useState(true)
  const [fromMonth, setFromMonth] = useState(currentMonth())
  const [toMonth, setToMonth] = useState(currentMonth())

  function setPreset(from: string, to: string) { setFromMonth(from); setToMonth(to) }

  function prevMonth() {
    const d = new Date(); d.setMonth(d.getMonth() - 1)
    return d.toISOString().slice(0, 7)
  }

  const presets = [
    { label: t('admin.stats.presetPrevMonth'), fn: () => setPreset(prevMonth(), prevMonth()) },
    { label: t('admin.stats.presetCurMonth'), fn: () => setPreset(currentMonth(), currentMonth()) },
    { label: t('admin.stats.presetCurYear'), fn: () => setPreset(new Date().getFullYear() + '-01', currentMonth()) },
    { label: t('admin.stats.presetPrevYear'), fn: () => { const y = new Date().getFullYear() - 1; setPreset(`${y}-01`, `${y}-12`) } },
    { label: t('admin.stats.presetAll'), fn: () => setPreset('', '') },
  ]

  const loadStats = useCallback(() => {
    setLoading(true)
    const params = new URLSearchParams()
    if (fromMonth) params.set('from', fromMonth)
    if (toMonth) params.set('to', toMonth)
    const qs = params.toString() ? `?${params}` : ''
    Promise.all([
      apiClient.get<StatsData>(`/api/manage/stats${qs}`),
      apiClient.get<ChargerStatsResponse>('/api/manage/stats/chargers'),
    ]).then(([s, c]) => {
      setStats(s)
      setChargers(c.items)
      setUtilization(c.utilization)
    }).finally(() => setLoading(false))
  }, [fromMonth, toMonth])

  useEffect(() => { loadStats() }, [loadStats])

  if (loading || !stats) return <p className="text-brand-400 py-4">{t('admin.loading')}</p>

  return (
    <div className="space-y-6">
      {/* Date range filter */}
      <div className="space-y-2">
        <div className="flex flex-wrap gap-1">
          {presets.map(p => (
            <button key={p.label} onClick={p.fn}
              className="text-xs px-3 py-1 rounded-lg border border-brand-100 text-brand-600 hover:bg-brand-50 transition-colors">
              {p.label}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <input type="month" value={fromMonth} onChange={e => setFromMonth(e.target.value)}
            className="rounded-lg border-brand-100 text-sm focus:border-brand-500 focus:ring-brand-500" />
          <span className="text-brand-400">—</span>
          <input type="month" value={toMonth} onChange={e => setToMonth(e.target.value)}
            className="rounded-lg border-brand-100 text-sm focus:border-brand-500 focus:ring-brand-500" />
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        <StatCard label={t('admin.stats.totalSessions')} value={stats.totals.sessions.toString()} />
        <StatCard label={t('admin.stats.totalEnergy')} value={formatKwh(stats.totals.kwh)} />
        <StatCard label={t('admin.stats.spotEnergy')}
          value={formatEur(stats.totals.eur - stats.totals.marginRevenue)}
          sub={`${t('admin.stats.avgPrice')}: ${stats.totals.avgSpotSntKwh} snt/kWh`} />
        <StatCard label={t('admin.stats.billedEnergy')}
          value={formatEur(stats.totals.eur)}
          sub={`${t('admin.stats.spotLabel')} ${formatEur(stats.totals.eur - stats.totals.marginRevenue)} + ${t('admin.stats.marginLabel')} ${formatEur(stats.totals.marginRevenue)}`} />
        <StatCard label={t('admin.stats.energyMargin')}
          value={formatEur(stats.totals.marginRevenue - stats.totals.distributionCost)}
          sub={`${t('admin.stats.marginLabel')} ${formatEur(stats.totals.marginRevenue)} - ${t('admin.stats.distribution')} ${formatEur(stats.totals.distributionCost)}`}
          color={(stats.totals.marginRevenue - stats.totals.distributionCost) >= 0 ? 'text-green-700' : 'text-red-600'} />
      </div>

      {/* Cost breakdown */}
      <div className="bg-white rounded-xl shadow-sm p-4">
        <h2 className="text-sm font-semibold text-brand-600 mb-3">{t('admin.stats.costBreakdown')}</h2>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 text-sm">
          <div>
            <p className="text-brand-400 text-xs">{t('admin.stats.marginRevenue')}</p>
            <p className="font-semibold text-green-700">{formatEur(stats.totals.marginRevenue)}</p>
            <p className="text-xs text-brand-400">{stats.costParams.marginCents} snt/kWh</p>
          </div>
          <div>
            <p className="text-brand-400 text-xs">{t('admin.stats.contractRevenue')}</p>
            <p className="font-semibold text-green-700">{formatEur(stats.totals.contractRevenue)}</p>
            <p className="text-xs text-brand-400">{stats.costParams.activeContracts} sop. × {formatEur(stats.costParams.contractFeeEur)}/kk</p>
          </div>
          <div>
            <p className="text-brand-400 text-xs">{t('admin.stats.distributionCost')}</p>
            <p className="font-semibold text-red-600">-{formatEur(stats.totals.distributionCost)}</p>
            <p className="text-xs text-brand-400">{stats.costParams.distributionCostSntKwh} snt/kWh</p>
          </div>
          <div>
            <p className="text-brand-400 text-xs">{t('admin.stats.baseFee')}</p>
            <p className="font-semibold text-red-600">-{formatEur(stats.totals.baseFee)}</p>
            <p className="text-xs text-brand-400">{formatEur(stats.costParams.monthlyBaseFeeEur)}/kk</p>
          </div>
          <div>
            <p className="text-brand-400 text-xs">{t('admin.stats.grossMargin')}</p>
            <p className={`font-bold text-lg ${stats.totals.grossMargin >= 0 ? 'text-green-700' : 'text-red-600'}`}>
              {formatEur(stats.totals.grossMargin)}
            </p>
          </div>
        </div>
      </div>

      {stats.hasEstimated && (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 text-sm px-4 py-2 rounded-lg">
          {t('dashboard.estimatedWarning')}
        </div>
      )}

      {/* Monthly breakdown table */}
      {stats.monthly.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm p-4">
          <h2 className="text-sm font-semibold text-brand-600 mb-3">{t('admin.stats.monthlyTrend')}</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-brand-500 text-xs border-b border-brand-100">
                  <th className="pb-2">{t('admin.stats.month')}</th>
                  <th className="pb-2 text-right">{t('admin.stats.contracts')}</th>
                  <th className="pb-2 text-right">{t('admin.stats.sessions')}</th>
                  <th className="pb-2 text-right">kWh</th>
                  <th className="pb-2 text-right">{t('admin.stats.usersBilled')}</th>
                  <th className="pb-2 text-right">{t('admin.stats.marginRevenue')}</th>
                  <th className="pb-2 text-right">{t('admin.stats.contractRevenue')}</th>
                  <th className="pb-2 text-right">{t('admin.stats.costs')}</th>
                  <th className="pb-2 text-right">{t('admin.stats.grossMargin')}</th>
                </tr>
              </thead>
              <tbody>
                {stats.monthly.map(m => (
                  <tr key={m.month} className="border-b border-brand-50">
                    <td className="py-2 font-medium text-brand-700">{m.month}</td>
                    <td className="py-2 text-right text-brand-600">{m.activeContracts}</td>
                    <td className="py-2 text-right text-brand-600">{m.sessions}</td>
                    <td className="py-2 text-right text-brand-600">{formatKwh(m.kwh)}</td>
                    <td className="py-2 text-right text-brand-600">{formatEur(m.eur)}</td>
                    <td className="py-2 text-right text-brand-600">{formatEur(m.marginRevenue)}</td>
                    <td className="py-2 text-right text-green-600">{formatEur(m.contractRevenue)}</td>
                    <td className="py-2 text-right text-red-500">-{formatEur(m.totalCost)}</td>
                    <td className={`py-2 text-right font-semibold ${m.grossMargin >= 0 ? 'text-green-700' : 'text-red-600'}`}>
                      {formatEur(m.grossMargin)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Charger breakdown table */}
      {chargers.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm p-4">
          <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
            <h2 className="text-sm font-semibold text-brand-600">{t('admin.stats.chargerBreakdown')}</h2>
            <span className="text-xs text-brand-500">
              {t('admin.stats.utilization')}: <span className="font-semibold text-brand-700">{utilization.used}/{utilization.total}</span>
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-brand-500 text-xs border-b border-brand-100">
                  <th className="pb-2">{t('admin.stats.charger')}</th>
                  <th className="pb-2 text-right">{t('admin.stats.sessions')}</th>
                  <th className="pb-2 text-right">kWh</th>
                  <th className="pb-2 text-right">{t('admin.stats.eur')}</th>
                </tr>
              </thead>
              <tbody>
                {chargers.map(c => (
                  <tr key={c.chargerId} className={`border-b border-brand-50 ${c.sessions === 0 ? 'opacity-60' : ''}`}>
                    <td className="py-2 font-medium text-brand-700">{c.chargerName}</td>
                    <td className="py-2 text-right text-brand-600">{c.sessions}</td>
                    <td className="py-2 text-right text-brand-600">{formatKwh(c.kwh)}</td>
                    <td className="py-2 text-right font-medium text-brand-700">{formatEur(c.eur)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}

function StatCard({ label, value, sub, color }: { label: string; value: string; sub?: string; color?: string }) {
  return (
    <div className="bg-white rounded-xl shadow-sm p-4 text-center">
      <p className="text-brand-400 text-xs uppercase tracking-wide mb-1">{label}</p>
      <p className={`text-xl font-bold ${color ?? 'text-brand-700'}`}>{value}</p>
      {sub && <p className="text-xs text-brand-400 mt-0.5">{sub}</p>}
    </div>
  )
}

// --- Reports Tab ---
function getQuarterOptions(): { value: string; label: string }[] {
  const options: { value: string; label: string }[] = []
  const now = new Date()
  const currentYear = now.getUTCFullYear()
  const currentQuarter = Math.ceil((now.getUTCMonth() + 1) / 3)
  const EARLIEST = '2026-Q1' // Lataus asennettu maaliskuussa 2026

  for (let i = 0; i < 8; i++) {
    let q = currentQuarter - i
    let y = currentYear
    while (q <= 0) { q += 4; y-- }
    const val = `${y}-Q${q}`
    if (val < EARLIEST) break
    options.push({ value: val, label: `${y} Q${q}` })
  }
  return options
}

interface ReportData {
  quarter: string
  startDate: string
  endDate: string
  totals: { sessions: number; kwh: number; eur: number }
  hasEstimated: boolean
  users: {
    userId: string; displayName: string; email: string; apartmentNumber: string
    sessions: number; kwh: number; eur: number; hasEstimated: boolean
    contracts: { type: string; parkingSpace: string; status: string }[]
  }[]
}

function ReportsTab() {
  const { t } = useTranslation()
  const quarterOptions = getQuarterOptions()
  const [quarter, setQuarter] = useState(quarterOptions[1]?.value ?? quarterOptions[0]?.value)
  const [report, setReport] = useState<ReportData | null>(null)
  const [loading, setLoading] = useState(false)

  const loadReport = useCallback(() => {
    setLoading(true)
    apiClient.get<ReportData>(`/api/manage/reports/quarterly?quarter=${quarter}`)
      .then(setReport)
      .finally(() => setLoading(false))
  }, [quarter])

  useEffect(() => { loadReport() }, [loadReport])

  function downloadCsv() {
    window.open(`${env.apiBaseUrl}/api/manage/reports/quarterly/csv?quarter=${quarter}`, '_blank')
  }

  function downloadReport() {
    window.open(`${env.apiBaseUrl}/api/manage/reports/quarterly/pdf?quarter=${quarter}`, '_blank')
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-4">
        <div>
          <label className="block text-xs text-brand-500 mb-1">{t('admin.reports.quarter')}</label>
          <select value={quarter} onChange={e => setQuarter(e.target.value)}
            className="rounded-lg border-brand-200 text-sm focus:border-brand-500 focus:ring-brand-500">
            {quarterOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
        <div className="flex gap-2 pt-4">
          <button onClick={downloadCsv}
            className="bg-brand-500 text-white text-sm px-4 py-1.5 rounded-lg hover:bg-brand-600 transition-colors">
            {t('admin.reports.downloadCsv')}
          </button>
          <button onClick={downloadReport}
            className="bg-brand-100 text-brand-700 text-sm px-4 py-1.5 rounded-lg hover:bg-brand-200 transition-colors">
            {t('admin.reports.downloadReport')}
          </button>
        </div>
      </div>

      {loading && <p className="text-brand-400 py-4">{t('admin.loading')}</p>}

      {report && !loading && (
        <>
          {/* Summary */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="bg-white rounded-xl shadow-sm p-4 text-center">
              <p className="text-brand-400 text-xs uppercase mb-1">{t('admin.reports.sessions')}</p>
              <p className="text-xl font-bold text-brand-700">{report.totals.sessions}</p>
            </div>
            <div className="bg-white rounded-xl shadow-sm p-4 text-center">
              <p className="text-brand-400 text-xs uppercase mb-1">{t('admin.reports.kwh')}</p>
              <p className="text-xl font-bold text-brand-700">{formatKwh(report.totals.kwh)}</p>
            </div>
            <div className="bg-white rounded-xl shadow-sm p-4 text-center">
              <p className="text-brand-400 text-xs uppercase mb-1">{t('admin.reports.eur')}</p>
              <p className="text-xl font-bold text-brand-700">{formatEur(report.totals.eur)}</p>
            </div>
          </div>

          {report.hasEstimated && (
            <div className="bg-amber-50 border border-amber-200 text-amber-800 text-sm px-4 py-2 rounded-lg">
              {t('dashboard.estimatedWarning')}
            </div>
          )}

          {/* User breakdown table */}
          {report.users.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm p-4">
              <h2 className="text-sm font-semibold text-brand-600 mb-3">{t('admin.reports.preview')}</h2>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-brand-500 text-xs border-b border-brand-100">
                      <th className="pb-2">{t('admin.reports.user')}</th>
                      <th className="pb-2">{t('admin.reports.apartment')}</th>
                      <th className="pb-2">{t('admin.reports.parkingSpaces')}</th>
                      <th className="pb-2 text-right">{t('admin.reports.sessions')}</th>
                      <th className="pb-2 text-right">{t('admin.reports.kwh')}</th>
                      <th className="pb-2 text-right">{t('admin.reports.eur')}</th>
                      <th className="pb-2 text-right">{t('admin.reports.avgPrice')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.users.map(u => (
                      <tr key={u.userId} className="border-b border-brand-50">
                        <td className="py-2">
                          <span className="font-medium text-brand-700">{u.displayName}</span>
                          <span className="text-brand-400 text-xs ml-1">{u.email}</span>
                        </td>
                        <td className="py-2">{u.apartmentNumber}</td>
                        <td className="py-2 text-xs text-brand-600">
                          {u.contracts?.map((c, i) => (
                            <span key={i}>
                              {c.parkingSpace || '–'}
                              {c.type === 'sub' && <span className="text-brand-400 ml-0.5">(ala)</span>}
                              {i < u.contracts.length - 1 && ', '}
                            </span>
                          ))}
                        </td>
                        <td className="py-2 text-right">{u.sessions}</td>
                        <td className="py-2 text-right">{formatKwh(u.kwh)}</td>
                        <td className="py-2 text-right font-medium">{formatEur(u.eur)}</td>
                        <td className="py-2 text-right text-brand-500">
                          {u.kwh > 0 ? `${(u.eur / u.kwh * 100).toFixed(2)} snt/kWh` : '–'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="font-semibold border-t border-brand-200">
                      <td className="py-2 text-brand-700" colSpan={3}>{t('admin.reports.total')}</td>
                      <td className="py-2 text-right">{report.totals.sessions}</td>
                      <td className="py-2 text-right">{formatKwh(report.totals.kwh)}</td>
                      <td className="py-2 text-right">{formatEur(report.totals.eur)}</td>
                      <td className="py-2 text-right text-brand-500">
                        {report.totals.kwh > 0 ? `${(report.totals.eur / report.totals.kwh * 100).toFixed(2)} snt/kWh` : '–'}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}

// --- Audit Log Tab ---
interface AuditEntry {
  rowKey: string
  eventType: string
  actorUserId: string
  targetUserId: string
  targetEntityId: string
  targetEntityType: string
  details: string
  timestamp: string
}

const AUDIT_EVENT_TYPES = [
  'login_success', 'login_failed', 'login_locked', 'logout',
  'user_registered', 'user_activated', 'user_deactivated', 'user_role_changed',
  'contract_created', 'contract_ended', 'contract_ended_by_user',
  'subuser_added', 'subuser_requested', 'subuser_activated', 'subuser_deactivated', 'subuser_removed',
  'session_unlinked_inactive', 'session_unlinked_unknown',
  'margin_changed', 'settings_changed', 'parking_space_created',
]

function AuditLogTab() {
  const { t } = useTranslation()
  const [items, setItems] = useState<AuditEntry[]>([])
  const [loading, setLoading] = useState(true)
  const [month, setMonth] = useState(() => {
    const d = new Date()
    return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`
  })
  const [eventType, setEventType] = useState('')

  const load = useCallback(() => {
    setLoading(true)
    const params = new URLSearchParams({ month })
    if (eventType) params.set('eventType', eventType)
    apiClient.get<{ items: AuditEntry[] }>(`/api/manage/auditlog?${params}`)
      .then(r => setItems(r.items))
      .finally(() => setLoading(false))
  }, [month, eventType])

  useEffect(() => { load() }, [load])

  function parseDetails(d: string): string {
    if (!d) return ''
    try {
      const obj = JSON.parse(d)
      return Object.entries(obj).map(([k, v]) => `${k}: ${v}`).join(', ')
    } catch {
      return d
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-4">
        <div>
          <label className="block text-xs text-brand-500 mb-1">{t('admin.auditLog.month')}</label>
          <input type="month" value={month} onChange={e => setMonth(e.target.value)}
            className="rounded-lg border-brand-200 text-sm focus:border-brand-500 focus:ring-brand-500" />
        </div>
        <div>
          <label className="block text-xs text-brand-500 mb-1">{t('admin.auditLog.eventType')}</label>
          <select value={eventType} onChange={e => setEventType(e.target.value)}
            className="rounded-lg border-brand-200 text-sm focus:border-brand-500 focus:ring-brand-500">
            <option value="">{t('admin.auditLog.all')}</option>
            {AUDIT_EVENT_TYPES.map(et => <option key={et} value={et}>{et}</option>)}
          </select>
        </div>
      </div>

      {loading && <p className="text-brand-400 py-4">{t('admin.loading')}</p>}

      {!loading && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-brand-500 text-xs border-b border-brand-100">
                <th className="pb-2">{t('admin.auditLog.time')}</th>
                <th className="pb-2">{t('admin.auditLog.event')}</th>
                <th className="pb-2">{t('admin.auditLog.actor')}</th>
                <th className="pb-2">{t('admin.auditLog.target')}</th>
                <th className="pb-2">{t('admin.auditLog.details')}</th>
              </tr>
            </thead>
            <tbody>
              {items.map(entry => (
                <tr key={entry.rowKey} className="border-b border-brand-50">
                  <td className="py-1.5 text-brand-700 whitespace-nowrap">
                    {entry.timestamp ? formatDateTimeLocal(entry.timestamp) : '-'}
                  </td>
                  <td className="py-1.5">
                    <span className="text-xs bg-brand-50 text-brand-600 px-2 py-0.5 rounded">{entry.eventType}</span>
                  </td>
                  <td className="py-1.5 text-brand-600 text-xs font-mono">
                    {entry.actorUserId ? entry.actorUserId.slice(0, 8) + '...' : '-'}
                  </td>
                  <td className="py-1.5 text-brand-600 text-xs">
                    {entry.targetUserId ? entry.targetUserId.slice(0, 8) + '...' : ''}
                    {entry.targetEntityId ? ` (${entry.targetEntityType}: ${entry.targetEntityId.slice(0, 8)}...)` : ''}
                  </td>
                  <td className="py-1.5 text-brand-500 text-xs max-w-xs truncate" title={parseDetails(entry.details)}>
                    {parseDetails(entry.details)}
                  </td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr>
                  <td colSpan={5} className="py-4 text-center text-brand-400">-</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

// --- Parking Tab ---
interface ParkingSpaceItem {
  rowKey: string
  spaceNumber: string
  description: string
  createdAt: string
  activeContract: {
    contractId: string
    userName: string
    apartmentNumber: string
    isPrimary: boolean
  } | null
}

function ParkingTab() {
  const { t } = useTranslation()
  const { showToast } = useToast()
  const [spaces, setSpaces] = useState<ParkingSpaceItem[]>([])
  const [loading, setLoading] = useState(true)
  const [showCreate, setShowCreate] = useState(false)
  const [form, setForm] = useState({ spaceNumber: '', description: '' })
  const [creating, setCreating] = useState(false)

  const load = useCallback(() => {
    setLoading(true)
    apiClient.get<{ items: ParkingSpaceItem[] }>('/api/manage/parking')
      .then(r => setSpaces(r.items))
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => { load() }, [load])

  async function submitCreate() {
    setCreating(true)
    try {
      await apiClient.post('/api/manage/parking', form)
      showToast(t('admin.parking.created'), 'success')
      setShowCreate(false)
      setForm({ spaceNumber: '', description: '' })
      load()
    } catch {
      showToast(t('errors.generic'), 'error')
    } finally {
      setCreating(false)
    }
  }

  if (loading) return <p className="text-brand-400 py-4">{t('admin.loading')}</p>

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-brand-600">{t('admin.parking.title')} ({spaces.length})</h3>
        <button onClick={() => { setShowCreate(true); setForm({ spaceNumber: '', description: '' }) }}
          className="bg-brand-500 text-white text-sm px-4 py-1.5 rounded-lg hover:bg-brand-600 transition-colors">
          {t('admin.parking.add')}
        </button>
      </div>

      <Modal open={showCreate} onClose={() => setShowCreate(false)} title={t('admin.parking.add')}>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-brand-700 mb-1">{t('admin.parking.number')}</label>
            <input type="text" value={form.spaceNumber} onChange={e => setForm(f => ({ ...f, spaceNumber: e.target.value }))}
              className="w-full rounded-lg border-brand-100 text-sm focus:border-brand-500 focus:ring-brand-500" />
          </div>
          <div>
            <label className="block text-sm font-medium text-brand-700 mb-1">{t('admin.parking.description')}</label>
            <input type="text" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              className="w-full rounded-lg border-brand-100 text-sm focus:border-brand-500 focus:ring-brand-500" />
          </div>
          <div className="flex gap-3 pt-2">
            <button onClick={submitCreate} disabled={creating || !form.spaceNumber}
              className="bg-brand-500 text-white text-sm px-5 py-2 rounded-lg hover:bg-brand-600 disabled:opacity-50 transition-colors">
              {t('admin.parking.add')}
            </button>
            <button onClick={() => setShowCreate(false)}
              className="text-brand-500 text-sm px-5 py-2 rounded-lg hover:bg-brand-50 transition-colors">
              {t('admin.users.cancel')}
            </button>
          </div>
        </div>
      </Modal>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-brand-500 text-xs border-b border-brand-100">
              <th className="pb-2">{t('admin.parking.number')}</th>
              <th className="pb-2">{t('admin.parking.user')}</th>
              <th className="pb-2">{t('admin.users.apartment')}</th>
              <th className="pb-2">{t('admin.parking.status')}</th>
            </tr>
          </thead>
          <tbody>
            {spaces.map(s => (
              <tr key={s.rowKey} className="border-b border-brand-50">
                <td className="py-2 font-bold text-brand-700">{s.spaceNumber}</td>
                <td className="py-2 text-brand-600">{s.activeContract?.userName ?? ''}</td>
                <td className="py-2 text-brand-600">{s.activeContract?.apartmentNumber ?? ''}</td>
                <td className="py-2">
                  {s.activeContract ? (
                    <span className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded">
                      {s.activeContract.isPrimary ? t('admin.contracts.primaryContract') : t('admin.contracts.subContract')}
                    </span>
                  ) : (
                    <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded">{t('admin.parking.free')}</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// --- Settings Tab ---
interface Settings {
  magicLinkAdminUserId: string
  notifyUserMgmt: string[]
  notifyUserMgmtName: string
  notifyBilling: string[]
  notifyBillingName: string
  reportRecipients: string[]
  notifyTechnical: string[]
  notifyAlerts: string[]
  notifyBoardStats: string[]
  defaultMarginCents: number
}

interface TimerInfo { name: string; description: string; interval: string; disabled?: boolean }

interface RunTimerResponse {
  name: string
  durationMs: number
  result: unknown
  logs: string[]
}

interface SystemStatus {
  zaptec: { lastSyncAt: string; failureCount: number; lastError: string | null; resolvedAt: string | null; ok: boolean }
  spotPrices: { source: string; failureCount: number; lastError: string | null; resolvedAt: string | null; todayPrices: number; tomorrowPrices: number; ok: boolean }
  backup: { lastDate: string; fileCount: number; totalSizeKb: number; ok: boolean }
  email: { sender: string; configured: boolean; ok: boolean }
  tables: Record<string, number>
  timers: TimerInfo[]
  environment: string
  serverTime: string
}

function StatusBadge({ ok, label }: { ok: boolean; label: string }) {
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs font-medium px-2 py-0.5 rounded ${ok ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
      <span className={`w-2 h-2 rounded-full ${ok ? 'bg-green-500' : 'bg-red-500'}`} />
      {label}
    </span>
  )
}

function SystemStatusTab() {
  const { t } = useTranslation()
  const { showToast } = useToast()
  const [status, setStatus] = useState<SystemStatus | null>(null)
  const [loading, setLoading] = useState(true)
  const [running, setRunning] = useState<string | null>(null)
  const [lastRun, setLastRun] = useState<RunTimerResponse | null>(null)

  useEffect(() => {
    apiClient.get<SystemStatus>('/api/manage/system-status')
      .then(setStatus)
      .finally(() => setLoading(false))
  }, [])

  async function runTimer(name: string) {
    if (running) return
    if (!confirm(t('admin.system.runConfirm', { name }))) return
    setRunning(name)
    setLastRun(null)
    try {
      const res = await apiClient.post<RunTimerResponse>('/api/manage/run-timer', { name })
      setLastRun(res)
      showToast(t('admin.system.runSuccess', { name, ms: res.durationMs }), 'success')
    } catch (err) {
      showToast((err as Error).message || t('errors.generic'), 'error')
    } finally {
      setRunning(null)
    }
  }

  if (loading || !status) return <p className="text-brand-400 py-4">{t('admin.loading')}</p>

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {/* Zaptec */}
        <div className="bg-white rounded-xl shadow-sm p-4 space-y-2">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-brand-700">Zaptec API</h3>
            <StatusBadge ok={status.zaptec.ok} label={status.zaptec.ok ? t('admin.system.ok') : t('admin.system.error')} />
          </div>
          <p className="text-xs text-brand-500">{t('admin.system.lastSync')}: {status.zaptec.lastSyncAt ? formatDateTimeLocal(status.zaptec.lastSyncAt) : '–'}</p>
          {status.zaptec.failureCount > 0 && (
            <p className="text-xs text-red-500">{t('admin.system.failures')}: {status.zaptec.failureCount}</p>
          )}
          {status.zaptec.resolvedAt && (
            <p className="text-xs text-green-600">{t('admin.system.resolved')}: {formatDateTimeLocal(status.zaptec.resolvedAt)}</p>
          )}
          {status.zaptec.lastError && <p className="text-xs text-red-400 truncate">{status.zaptec.lastError}</p>}
        </div>

        {/* Spot prices */}
        <div className="bg-white rounded-xl shadow-sm p-4 space-y-2">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-brand-700">{t('admin.system.spotPrices')}</h3>
            <StatusBadge ok={status.spotPrices.ok} label={status.spotPrices.ok ? t('admin.system.ok') : t('admin.system.incomplete')} />
          </div>
          <p className="text-xs text-brand-400">{status.spotPrices.source}</p>
          <p className="text-xs text-brand-500">{t('admin.system.today')}: {status.spotPrices.todayPrices} {t('admin.system.pricePoints')}</p>
          <p className="text-xs text-brand-500">{t('admin.system.tomorrow')}: {status.spotPrices.tomorrowPrices} {t('admin.system.pricePoints')}</p>
          {status.spotPrices.resolvedAt && (
            <p className="text-xs text-green-600">{t('admin.system.resolved')}: {formatDateTimeLocal(status.spotPrices.resolvedAt)}</p>
          )}
          {status.spotPrices.failureCount > 0 && (
            <p className="text-xs text-red-500">{t('admin.system.failures')}: {status.spotPrices.failureCount}</p>
          )}
          {status.spotPrices.lastError && <p className="text-xs text-red-400 truncate">{status.spotPrices.lastError}</p>}
        </div>

        {/* Backup */}
        <div className="bg-white rounded-xl shadow-sm p-4 space-y-2">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-brand-700">{t('admin.system.backup')}</h3>
            <StatusBadge ok={status.backup.ok} label={status.backup.ok ? t('admin.system.ok') : t('admin.system.outdated')} />
          </div>
          <p className="text-xs text-brand-500">{t('admin.system.lastBackup')}: {status.backup.lastDate || '–'}</p>
          <p className="text-xs text-brand-500">{status.backup.fileCount} {t('admin.system.files')}, {status.backup.totalSizeKb} KB</p>
        </div>

        {/* Email */}
        <div className="bg-white rounded-xl shadow-sm p-4 space-y-2">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-brand-700">{t('admin.system.email')}</h3>
            <StatusBadge ok={status.email.ok} label={status.email.ok ? t('admin.system.ok') : t('admin.system.notConfigured')} />
          </div>
          <p className="text-xs text-brand-500">{t('admin.system.sender')}: {status.email.sender || '–'}</p>
        </div>
      </div>

      {/* Table row counts */}
      <div className="bg-white rounded-xl shadow-sm p-4">
        <h3 className="text-sm font-semibold text-brand-700 mb-3">{t('admin.system.database')}</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
          {Object.entries(status.tables).map(([table, count]) => (
            <div key={table}>
              <p className="text-brand-400 text-xs">{table}</p>
              <p className="font-medium text-brand-700">{count} {t('admin.system.rows')}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Timers */}
      <div className="bg-white rounded-xl shadow-sm p-4">
        <h3 className="text-sm font-semibold text-brand-700 mb-3">{t('admin.system.timers')}</h3>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-brand-500 text-xs border-b border-brand-100">
              <th className="pb-2">{t('admin.system.timerName')}</th>
              <th className="pb-2">{t('admin.system.timerInterval')}</th>
              <th className="pb-2 text-right">{t('admin.system.timerAction')}</th>
            </tr>
          </thead>
          <tbody>
            {status.timers.map(timer => (
              <tr key={timer.name} className="border-b border-brand-50">
                <td className="py-2">
                  <div className="flex items-center gap-2">
                    <p className="font-medium text-brand-700">{timer.description}</p>
                    {timer.disabled && (
                      <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200">
                        {t('admin.system.timerDisabled')}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-brand-400">{timer.name}</p>
                </td>
                <td className="py-2 text-brand-600">{timer.interval}</td>
                <td className="py-2 text-right">
                  <button
                    type="button"
                    onClick={() => runTimer(timer.name)}
                    disabled={running !== null}
                    className="bg-blue-500 hover:bg-blue-600 text-white text-xs font-medium px-3 py-1.5 rounded-lg
                      disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                    {running === timer.name ? t('admin.loading') : t('admin.system.runNow')}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {lastRun && (
          <div className="mt-4 bg-brand-50 rounded-lg p-3 text-xs">
            <p className="font-medium text-brand-700 mb-1">
              {t('admin.system.runResult', { name: lastRun.name, ms: lastRun.durationMs })}
            </p>
            {lastRun.logs.length > 0 && (
              <pre className="whitespace-pre-wrap text-brand-600 max-h-48 overflow-auto">
                {lastRun.logs.join('\n')}
              </pre>
            )}
            <details className="mt-2">
              <summary className="cursor-pointer text-brand-500">{t('admin.system.runResultDetails')}</summary>
              <pre className="whitespace-pre-wrap text-brand-600 mt-1">
                {JSON.stringify(lastRun.result, null, 2)}
              </pre>
            </details>
          </div>
        )}
      </div>

      <p className="text-xs text-brand-400">
        {t('admin.system.env')}: {status.environment} · {t('admin.system.serverTimeLabel')}: {formatDateTimeLocal(status.serverTime)}
      </p>
    </div>
  )
}

const ARRAY_KEYS = ['notifyUserMgmt', 'notifyBilling', 'reportRecipients', 'notifyTechnical', 'notifyAlerts', 'notifyBoardStats'] as const
type ArrayKey = typeof ARRAY_KEYS[number]

function SettingsTab() {
  const { t } = useTranslation()
  const [settings, setSettings] = useState<Settings | null>(null)
  const [rawArrays, setRawArrays] = useState<Record<ArrayKey, string>>({
    notifyUserMgmt: '', notifyBilling: '', reportRecipients: '', notifyTechnical: '', notifyAlerts: '', notifyBoardStats: '',
  })
  const [adminUsers, setAdminUsers] = useState<{ rowKey: string; displayName: string; email: string }[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    Promise.all([
      apiClient.get<Settings>('/api/manage/settings'),
      apiClient.get<{ items: { rowKey: string; displayName: string; email: string; is_admin: boolean }[] }>('/api/manage/users'),
    ]).then(([s, u]) => {
      setSettings(s)
      setRawArrays({
        notifyUserMgmt: (s.notifyUserMgmt || []).join(', '),
        notifyBilling: (s.notifyBilling || []).join(', '),
        reportRecipients: (s.reportRecipients || []).join(', '),
        notifyTechnical: (s.notifyTechnical || []).join(', '),
        notifyAlerts: (s.notifyAlerts || []).join(', '),
        notifyBoardStats: (s.notifyBoardStats || []).join(', '),
      })
      setAdminUsers(u.items.filter(usr => usr.is_admin))
    }).finally(() => setLoading(false))
  }, [])

  async function save() {
    if (!settings) return
    setSaving(true)
    setSaved(false)
    setError(null)
    // Parse raw array inputs into arrays at save time (not on every keystroke)
    const parsed: Partial<Record<ArrayKey, string[]>> = {}
    for (const key of ARRAY_KEYS) {
      parsed[key] = rawArrays[key].split(',').map(s => s.trim()).filter(Boolean)
    }
    const payload = { ...settings, ...parsed }
    try {
      await apiClient.put('/api/manage/settings', payload)
      setSettings(payload as Settings)
      setSaved(true)
      setTimeout(() => setSaved(false), 3000)
    } catch (err) {
      const e = err as Error & { body?: { message?: string } }
      setError(e.body?.message || e.message || t('admin.settings.saveFailed'))
    } finally {
      setSaving(false)
    }
  }

  if (loading || !settings) return <p className="text-brand-400 py-4">{t('admin.loading')}</p>

  function updateArr(key: ArrayKey, value: string) {
    setRawArrays(prev => ({ ...prev, [key]: value }))
  }

  function updateStr(key: keyof Settings, value: string) {
    setSettings(prev => prev ? { ...prev, [key]: value } : prev)
  }

  // updateNum removed — margin moved to Pricing tab

  return (
    <div className="space-y-6 max-w-lg">
      <SettingsGroup title={t('admin.settings.userMgmt')}>
        <div>
          <label className="block text-xs text-brand-500 mb-1">{t('admin.settings.magicLinkAdmin')}</label>
          <select value={settings.magicLinkAdminUserId || ''}
            onChange={e => updateStr('magicLinkAdminUserId', e.target.value)}
            className="w-full rounded-lg border-brand-100 text-sm focus:border-brand-500 focus:ring-brand-500">
            <option value="">{t('admin.settings.magicLinkAdminAuto')}</option>
            {adminUsers.map(u => (
              <option key={u.rowKey} value={u.rowKey}>{u.displayName} ({u.email})</option>
            ))}
          </select>
          <p className="text-xs text-brand-400 mt-1">{t('admin.settings.magicLinkAdminHelp')}</p>
        </div>
        <SettingsField label={t('admin.settings.recipients')} value={rawArrays.notifyUserMgmt}
          onChange={v => updateArr('notifyUserMgmt', v)} />
        <SettingsField label={t('admin.settings.emailName')} value={settings.notifyUserMgmtName}
          onChange={v => updateStr('notifyUserMgmtName', v)} />
      </SettingsGroup>

      <SettingsGroup title={t('admin.settings.billing')}>
        <SettingsField label={t('admin.settings.billingRecipients')} value={rawArrays.notifyBilling}
          onChange={v => updateArr('notifyBilling', v)} />
        <SettingsField label={t('admin.settings.emailName')} value={settings.notifyBillingName}
          onChange={v => updateStr('notifyBillingName', v)} />
        <SettingsField label={t('admin.settings.reportCopies')} value={rawArrays.reportRecipients}
          onChange={v => updateArr('reportRecipients', v)} />
      </SettingsGroup>

      <SettingsGroup title={t('admin.settings.technicalAlerts')}>
        <SettingsField label={t('admin.settings.alertRecipients')} value={rawArrays.notifyTechnical}
          onChange={v => updateArr('notifyTechnical', v)} />
      </SettingsGroup>

      <SettingsGroup title={t('admin.settings.operationalAlerts')}>
        <SettingsField label={t('admin.settings.alertRecipients')} value={rawArrays.notifyAlerts}
          onChange={v => updateArr('notifyAlerts', v)} />
      </SettingsGroup>

      <SettingsGroup title={t('admin.settings.boardStats')}>
        <SettingsField label={t('admin.settings.boardStatsRecipients')} value={rawArrays.notifyBoardStats}
          onChange={v => updateArr('notifyBoardStats', v)} />
        <p className="text-xs text-brand-400">{t('admin.settings.boardStatsHelp')}</p>
      </SettingsGroup>

      <div className="flex items-center gap-3">
        <button onClick={save} disabled={saving}
          className="bg-brand-500 text-white px-6 py-2 rounded-lg hover:bg-brand-600 disabled:opacity-50 transition-colors text-sm font-medium">
          {saving ? t('admin.settings.saving') : t('admin.settings.save')}
        </button>
        {saved && <span className="text-green-600 text-sm">{t('admin.settings.saved')}</span>}
        {error && <span className="text-red-600 text-sm">{error}</span>}
      </div>
    </div>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-sm font-medium text-brand-700 mb-1">{label}</label>
      {children}
    </div>
  )
}

function SettingsGroup({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h3 className="text-sm font-semibold text-brand-700 mb-2">{title}</h3>
      <div className="space-y-3">{children}</div>
    </div>
  )
}

function SettingsField({ label, value, onChange, type = 'text' }: {
  label: string; value: string; onChange: (v: string) => void; type?: string
}) {
  return (
    <div>
      <label className="block text-xs text-brand-500 mb-1">{label}</label>
      <input type={type} value={value} onChange={e => onChange(e.target.value)}
        className="w-full rounded-lg border-brand-100 text-sm focus:border-brand-500 focus:ring-brand-500" />
    </div>
  )
}

// --- Release Notes Tab ---
interface ReleaseNoteRow {
  version: string
  releaseDate: string
  title: string
  body: string
  createdAt: string
  updatedAt: string
}

function parseVersion(v: string): { major: number; minor: number; patch: number } {
  const clean = v.startsWith('v') ? v.slice(1) : v
  const [maj, min, p] = clean.split('.').map(n => parseInt(n, 10) || 0)
  return { major: maj, minor: min, patch: p }
}

function ReleaseNotesTab() {
  const { t } = useTranslation()
  const { showToast } = useToast()
  const [notes, setNotes] = useState<ReleaseNoteRow[]>([])
  const [loading, setLoading] = useState(true)
  const [editing, setEditing] = useState<ReleaseNoteRow | null>(null)
  const [creating, setCreating] = useState(false)
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set())

  const load = useCallback(() => {
    setLoading(true)
    apiClient.get<{ items: ReleaseNoteRow[] }>('/api/manage/release-notes')
      .then(r => setNotes(r.items))
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => { load() }, [load])

  // Group by major.minor
  const groups = (() => {
    const map = new Map<string, ReleaseNoteRow[]>()
    for (const n of notes) {
      const { major, minor } = parseVersion(n.version)
      const key = `${major}.${minor}`
      if (!map.has(key)) map.set(key, [])
      map.get(key)!.push(n)
    }
    // Sort patch desc inside each group
    for (const arr of map.values()) {
      arr.sort((a, b) => {
        const va = parseVersion(a.version), vb = parseVersion(b.version)
        return vb.patch - va.patch
      })
    }
    // Sort groups desc by major.minor
    const sortedKeys = [...map.keys()].sort((a, b) => {
      const [am, an] = a.split('.').map(Number)
      const [bm, bn] = b.split('.').map(Number)
      return bm !== am ? bm - am : bn - an
    })
    return sortedKeys.map(k => ({ key: k, items: map.get(k)! }))
  })()

  // Default: expand the first (newest) group on first load
  useEffect(() => {
    if (groups.length > 0 && expandedGroups.size === 0) {
      setExpandedGroups(new Set([groups[0].key]))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notes.length])

  function toggleGroup(key: string) {
    setExpandedGroups(prev => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  async function deleteNote(version: string) {
    if (!confirm(t('admin.releasenotes.confirmDelete', { version }))) return
    try {
      await apiClient.delete(`/api/manage/release-notes/${encodeURIComponent(version)}`)
      showToast(t('admin.releasenotes.deleted'), 'success')
      load()
    } catch (e) {
      showToast((e as Error).message || t('errors.generic'), 'error')
    }
  }

  if (loading) return <p className="text-brand-400 py-4">{t('admin.loading')}</p>

  return (
    <div className="space-y-4 max-w-3xl">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-brand-700">{t('admin.releasenotes.title')}</h2>
        <button onClick={() => setCreating(true)}
          className="bg-brand-500 text-white text-sm px-4 py-2 rounded-lg hover:bg-brand-600 transition-colors">
          {t('admin.releasenotes.new')}
        </button>
      </div>

      {notes.length === 0 ? (
        <p className="text-brand-400 text-sm py-4">{t('admin.releasenotes.empty')}</p>
      ) : (
        <div className="space-y-2">
          {groups.map(g => {
            const expanded = expandedGroups.has(g.key)
            return (
              <div key={g.key} className="bg-white rounded-xl shadow-sm overflow-hidden">
                <button onClick={() => toggleGroup(g.key)}
                  className="w-full flex items-center justify-between px-4 py-3 hover:bg-brand-50 transition-colors">
                  <span className="font-semibold text-brand-700">v{g.key}</span>
                  <span className="text-xs text-brand-500">
                    {g.items.length} {t('admin.releasenotes.releases')} · {expanded ? '▾' : '▸'}
                  </span>
                </button>
                {expanded && (
                  <div className="border-t border-brand-100">
                    {g.items.map(n => (
                      <div key={n.version} className="px-4 py-3 border-b border-brand-50 last:border-b-0">
                        <div className="flex items-center justify-between gap-2 mb-2">
                          <div>
                            <span className="font-medium text-brand-700">{n.version}</span>
                            {n.title && <span className="text-brand-600 text-sm ml-2">— {n.title}</span>}
                            <span className="text-xs text-brand-400 ml-2">{n.releaseDate}</span>
                          </div>
                          <div className="flex gap-2">
                            <button onClick={() => setEditing(n)}
                              className="text-xs text-brand-500 hover:text-brand-700">
                              {t('admin.releasenotes.edit')}
                            </button>
                            <button onClick={() => deleteNote(n.version)}
                              className="text-xs text-red-500 hover:text-red-700">
                              {t('admin.releasenotes.delete')}
                            </button>
                          </div>
                        </div>
                        <div className="prose prose-sm max-w-none text-brand-700"
                          dangerouslySetInnerHTML={{ __html: sanitizeHtml(n.body) }} />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}

      {(creating || editing) && (
        <ReleaseNoteEditor
          initial={editing}
          onClose={() => { setCreating(false); setEditing(null) }}
          onSaved={() => { setCreating(false); setEditing(null); load() }}
        />
      )}
    </div>
  )
}

function ReleaseNoteEditor({ initial, onClose, onSaved }: {
  initial: ReleaseNoteRow | null
  onClose: () => void
  onSaved: () => void
}) {
  const { t } = useTranslation()
  const { showToast } = useToast()
  const [version, setVersion] = useState(initial?.version ?? '')
  const [releaseDate, setReleaseDate] = useState(initial?.releaseDate ?? new Date().toISOString().slice(0, 10))
  const [title, setTitle] = useState(initial?.title ?? '')
  const [body, setBody] = useState(initial?.body ?? '')
  const [saving, setSaving] = useState(false)
  const isEdit = !!initial

  async function save() {
    setSaving(true)
    try {
      if (isEdit) {
        await apiClient.put(`/api/manage/release-notes/${encodeURIComponent(initial!.version)}`,
          { releaseDate, title, body })
      } else {
        await apiClient.post('/api/manage/release-notes', { version, releaseDate, title, body })
      }
      showToast(t('admin.releasenotes.saved'), 'success')
      onSaved()
    } catch (e) {
      showToast((e as Error).message || t('errors.generic'), 'error')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal open={true} onClose={onClose} title={isEdit ? t('admin.releasenotes.editTitle') : t('admin.releasenotes.newTitle')}>
      <div className="space-y-3">
        <Field label={t('admin.releasenotes.version')}>
          <input type="text" value={version} disabled={isEdit}
            onChange={e => setVersion(e.target.value)} placeholder="v1.2.3"
            className="w-full rounded-lg border-brand-100 text-sm disabled:bg-brand-50" />
        </Field>
        <Field label={t('admin.releasenotes.releaseDate')}>
          <input type="date" value={releaseDate}
            onChange={e => setReleaseDate(e.target.value)}
            className="w-full rounded-lg border-brand-100 text-sm" />
        </Field>
        <Field label={t('admin.releasenotes.titleLabel')}>
          <input type="text" value={title}
            onChange={e => setTitle(e.target.value)}
            placeholder={t('admin.releasenotes.titlePlaceholder')}
            className="w-full rounded-lg border-brand-100 text-sm" />
        </Field>
        <Field label={t('admin.releasenotes.body')}>
          <ReactQuill theme="snow" value={body} onChange={setBody}
            modules={{ toolbar: [
              [{ header: [2, 3, false] }],
              ['bold', 'italic', 'underline'],
              [{ list: 'ordered' }, { list: 'bullet' }],
              ['link'],
              ['clean'],
            ] }} />
        </Field>
        <div className="flex gap-3 pt-2">
          <button onClick={save} disabled={saving || !version || !releaseDate || !body}
            className="bg-brand-500 text-white text-sm px-5 py-2 rounded-lg hover:bg-brand-600 disabled:opacity-50 transition-colors">
            {saving ? t('admin.settings.saving') : t('admin.releasenotes.save')}
          </button>
          <button onClick={onClose}
            className="text-brand-500 text-sm px-5 py-2 rounded-lg hover:bg-brand-50 transition-colors">
            {t('admin.users.cancel')}
          </button>
        </div>
      </div>
    </Modal>
  )
}
